import { createStore } from "vuex";

import { CONTRACTS, DRAFT_CONTRACTS, activityLevelTotalHaToolTip } from "../managrx/constants";
import {
  getASByID,
  getDraftASByID,
  makeSingleActivitySliceInfoGrid,
  makeSingleDraftActivitySliceInfoGrid,
  kRowsToItems,
  getSliceProductsList,
  updateSingleActivitySliceCache,
  updateSingleDraftActivitySliceCache,
} from "../managrx/services.js";
import { keysToCamel } from "../managrx/objectUtils.js";
import {
  retrieveDraftMultiResourceUtilization,
  retrieveMultiResourceUtilization,
  retrieveCampaignMultiResourceUtilization,
} from "../managrx/resourceUtilization";
import {
  alphabetized,
  formatRowsToLocaleString,
  parseToLocaleString,
} from "../managrx/utils.js";
import {
  getRequest,
  putRequest,
  postRequest,
  deleteRequest,
} from "../managrx/serverCall";
import { checkNotificationStructure } from "../managrx/errorHandler.js";

import {
  getDraftLock,
} from "../managrx/draftLock";
// Helper function to identify if a value might be an ID (basic heuristic for array-based data)
function isLikelyId(value, index) {
  // If it's a whole number greater than 0 and less than a reasonable ID limit
  // and it's in the first few columns, it might be an ID
  if (Number.isInteger(value) && value > 0 && value < 1000000 && index < 3) {
    return true;
  }
  return false;
}

function processViewObject(viewObject) {
  viewObject.rows = viewObject.rows.map((row) =>
    row.map((value, index) => {
      if (typeof value === "number" && !isLikelyId(value, index)) {
        return value.toLocaleString("en-US", {
          timeZone: "America/Argentina/Buenos_Aires",
        });
      } else {
        return value;
      }
    })
  );
  return viewObject;
}

// Several components mount at the same time and each dispatch the same
// reference-data actions (cost items, interest rates, payment policies...).
// Share a single in-flight request per action instead of firing duplicates.
const inFlightActions = new Map();
function dedupeInFlight(name, fn) {
  let existing = inFlightActions.get(name);
  if (existing) {
    return existing;
  }
  let promise = Promise.resolve()
    .then(fn)
    .finally(() => {
      inFlightActions.delete(name);
    });
  inFlightActions.set(name, promise);
  return promise;
}

// Stale-while-revalidate cache for the draft activity slice summary: the
// backend recomputes this view on every request (several seconds), but the
// data rarely changes, so the last known value is shown until the fresh
// response arrives.
function draftSummaryCacheKey(draftActivitySliceId) {
  return `draftASSummary:${draftActivitySliceId}`;
}
function readCachedDraftSummary(draftActivitySliceId) {
  try {
    let cached = sessionStorage.getItem(
      draftSummaryCacheKey(draftActivitySliceId)
    );
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}
function writeCachedDraftSummary(draftActivitySliceId, summary) {
  if (!draftActivitySliceId || !summary) {
    return;
  }
  try {
    sessionStorage.setItem(
      draftSummaryCacheKey(draftActivitySliceId),
      JSON.stringify(summary)
    );
  } catch {
    // Storage unavailable or full; caching is best-effort.
  }
}

// import { createLogger } from "vuex";

export default createStore({
  // plugins: [createLogger()],
  state: {
    sideBarDrawer: false,
    apiKeys: [],
    singleEngagementSummary: {},
    notificationsQueue: [],
    views: [],
    viewObject: {},
    userTokenInfo: {},
    isUserLog: false,
    summary_by_activity: { rows: [], keys: [] },
    summary_by_region: { rows: [], keys: [] },
    simFactors: { rows: [], keys: [] },
    forwardPrices: { rows: [], keys: [] },
    campaignProductsList: null,
    sliceProductsList: null,
    availableViewsLoading: false,
    viewObjectLoading: false,
    summariesLoading: false,
    activitySlices: [],
    draftActivitySlices: [],
    campaignEngagements: { key: [], items: [] },
    draftEngagements: { key: [], items: [] },
    draftEngagementsAttachmentAnalysis: [], // Initialize as empty array
    singleActivitySlice: {},
    singleDraftActivitySlice: {},
    singleDraftActivitySliceCashflow: {},
    isPageLoading: false,
    sliceInputCostPeriods: [],
    establishments: [],
    singleEstablishment: {},
    regions: [],
    activities: [],
    species: [],
    campaigns: [],
    singleAbstractCampaign: {},
    abstractCampaigns: [],
    campaignActivities: [],
    campaignActivitySlices: [],
    aggEngagementT3Metrics: [],
    aggDraftEngagementT3Metrics: [],
    engagementActivitySlices: [],
    engagementT3MetricsData: [], // Data from view endpoint for t3Metrics table
    draftEngagementActivitySlices: [],
    draftEngagementT3MetricsData: [], // Data from view endpoint for t3Metrics table
    activityPricesFromCampaign: [],
    loadingQueueStatus: 0,
    campaignModifiers: [],
    filteredCampaignModifiers: [],
    yieldModifiers: [],
    priceModifiers: [],
    costItemFamilyModifiers: [],
    freightModifiers: [],
    rentModifiers: [],
    singleCampaign: {},
    singleEngagement: {},
    singleEngagementLineage: {},
    singleDraftEngagement: {},
    singleDraftEngagementMetrics: {},
    singleEngagementMetrics: {},
    homepageMetrics: {},
    directSliceCostItems: [],
    costItemFamilies: [],
    costItemGroups: [],
    costItemGroupElements: [],
    financialPolicies: [],
    interestRates: [],
    otherTonCostItems: [],
    singleActivitySliceEconomicCashflow: {},
    activitySlicesEconomicCashflow: [],
    singleActivitySliceCashflow: {},
    engagementCashflow: {},
    campaignCashflow: {},
    campaignMetrics: {},
    campaignAdjustedMaxInv: null,
    drilledDownCampaignMetrics: {},
    campaignSimulationMetrics: {},
    campaignSimulationT3Table: {},
    draftEngagementCashflow: {},
    engagementResourceUtilization: {},
    draftEngagementResourceUtilization: {},
    campaignResourceUtilization: {},
    draftActivitySlice: {},
    draftEngagementsToCompare: [],
    isDraftEngagementCompareActive: false,
    t1MetricsComparison: [],
    t4MetricsComparison: [],
    t3MetricsComparison: [],
    campaignVersionsToCompare: [],
    t1MetricsCampaignComparison: [],
    t4MetricsCampaignComparison: [],
    t3MetricsCampaignComparison: [],
    cartesianProductTerms: {},
    allUsers: [],
    users: [],
    managers: [],
    directors: [],
    singleActivity: {},
    singleSpecies: {},
    costInputPeriods: [],
    activitySliceOutOfRangeAnalysis: [],
    draftActivitySliceOutOfRangeAnalysis: [],
    metricsFontSize: localStorage.getItem("tamanoDeLetraMetrics") || "M",
  },
  getters: {
    metricsFontSize: (state) => {
      return state.metricsFontSize;
    },
    allManagers: (state) => {
      return state.managers;
    },
    allDirectors: (state) => {
      return state.directors;
    },
    users: (state) => {
      return state.users;
    },
    userTokenInfo: (state) => {
      return state.userTokenInfo;
    },
    campaignActivities: (state) => {
      return state.campaignActivities;
    },
    campaignActivitySlices: (state) => {
      return state.campaignActivitySlices;
    },
    aggEngagementT3Metrics: (state) => {
      return state.aggEngagementT3Metrics;
    },
    aggDraftEngagementT3Metrics: (state) => {
      return state.aggDraftEngagementT3Metrics;
    },
    engagementActivitySlices: (state) => {
      return state.engagementActivitySlices;
    },
    engagementT3MetricsData: (state) => {
      return state.engagementT3MetricsData;
    },
    draftEngagementActivitySlices: (state) => {
      return state.draftEngagementActivitySlices;
    },
    draftEngagementT3MetricsData: (state) => {
      return state.draftEngagementT3MetricsData;
    },
    activityPricesFromCampaign: (state) => {
      return state.activityPricesFromCampaign;
    },
    notificationsQueue: (state) => {
      return state.notificationsQueue;
    },
    t1MetricsComparison: (state) => {
      return state.t1MetricsComparison;
    },
    t4MetricsComparison: (state) => {
      return state.t4MetricsComparison;
    },
    t3MetricsComparison: (state) => {
      return state.t3MetricsComparison;
    },
    t1MetricsCampaignComparison: (state) => {
      return state.t1MetricsCampaignComparison;
    },
    t4MetricsCampaignComparison: (state) => {
      return state.t4MetricsCampaignComparison;
    },
    t3MetricsCampaignComparison: (state) => {
      return state.t3MetricsCampaignComparison;
    },
    isDraftEngagementCompareActive: (state) => {
      return state.isDraftEngagementCompareActive;
    },
    draftEngagementsToCompare: (state) => {
      return state.draftEngagementsToCompare;
    },
    campaignVersionsToCompare: (state) => {
      return state.campaignVersionsToCompare;
    },
    singleEngagementSummary: (state) => {
      return state.singleEngagementSummary;
    },
    singleEngagementLineage: (state) => {
      return state.singleEngagementLineage;
    },
    sideBarDrawer: (state) => {
      return state.sideBarDrawer;
    },
    availableViews: (state) => {
      return state.views;
    },
    isAvailableViewsLoading(state) {
      return state.availableViewsLoading;
    },
    isViewObjectLoading(state) {
      return state.viewObjectLoading;
    },
    isSummaryLoading(state) {
      return state.summariesLoading;
    },
    viewObject(state) {
      return state.viewObject;
    },
    summaryByRegion(state) {
      return state.summary_by_region;
    },
    summaryByActivity(state) {
      return state.summary_by_activity;
    },
    summaryByBusinessActivity(state) {
      return state.summary_by_activity;
    },
    simulationFactors(state) {
      return state.simFactors;
    },
    forwardPrices(state) {
      return state.forwardPrices;
    },
    activitySlices(state) {
      return state.activitySlices;
    },
    draftActivitySlices(state) {
      return state.draftActivitySlices;
    },
    campaignModifiers(state) {
      return state.campaignModifiers;
    },
    filteredCampaignModifiers(state) {
      return state.filteredCampaignModifiers;
    },
    campaignEngagements(state) {
      return state.campaignEngagements;
    },
    yieldModifiers(state) {
      return state.yieldModifiers;
    },
    priceModifiers(state) {
      return state.priceModifiers;
    },
    costItemFamilyModifiers(state) {
      return state.costItemFamilyModifiers;
    },
    freightModifiers(state) {
      return state.freightModifiers;
    },
    rentModifiers(state) {
      return state.rentModifiers;
    },
    draftEngagements(state) {
      return state.draftEngagements;
    },
    draftEngagementsAttachmentAnalysis(state) {
      return state.draftEngagementsAttachmentAnalysis;
    },
    singleActivitySlice(state) {
      return state.singleActivitySlice;
    },
    singleDraftActivitySlice(state) {
      return state.singleDraftActivitySlice;
    },
    singleDraftActivitySliceCashflow(state) {
      return state.singleDraftActivitySliceCashflow;
    },
    singleActivitySliceCashflow(state) {
      return state.singleActivitySliceCashflow;
    },
    isPageLoading(state) {
      return state.isPageLoading;
    },
    establishments(state) {
      return state.establishments;
    },
    singleEstablishment(state) {
      return state.singleEstablishment;
    },
    regions(state) {
      return state.regions;
    },
    otherTonCostItems(state) {
      return state.otherTonCostItems;
    },

    sliceCostItems(state) {
      let singleActivitySlice = state?.singleActivitySlice;
      let assignedSCostItems = singleActivitySlice.assignedSCostItems;
      let keys = [
        { text: "itemCost", value: "itemCost" },
        { text: "unitsPerHectare", value: "unitsPerHectare" },
        { text: "price", value: "price" },
        { text: "costItemNaturalOrder", value: "costItemNaturalOrder" },
        { text: "Fecha de uso", value: "dateUsed" },
        { text: "Nombre", value: "directSliceCostItemName" },
        { text: "Unidad de Medida", value: "unitMeasure" },
        { text: "periodType", value: "periodType" },
        { text: "percentApplication", value: "percentApplication" },
        { text: "Familia de Elemento de Costo", value: "costItemFamilyName" },
      ];
      return {
        keys: keys,
        rows: assignedSCostItems,
      };
    },
    draftSliceCostItems(state) {
      let singleDraftActivitySlice = state?.singleDraftActivitySlice;
      let assignedSCostItems = singleDraftActivitySlice.assignedSCostItems;
      let keys = [
        { text: "itemCost", value: "itemCost" },
        { text: "unitsPerHectare", value: "unitsPerHectare" },
        { text: "price", value: "price" },
        { text: "costItemNaturalOrder", value: "costItemNaturalOrder" },
        { text: "Fecha de uso", value: "dateUsed" },
        { text: "Nombre", value: "directSliceCostItemName" },
        { text: "Unidad de Medida", value: "unitMeasure" },
        { text: "periodType", value: "periodType" },
        { text: "percentApplication", value: "percentApplication" },
        { text: "Familia de Elemento de Costo", value: "costItemFamilyName" },
      ];
      return {
        keys: keys,
        rows: assignedSCostItems,
      };
    },
    singleActivitySliceInfoGrid(state) {
      try {
        let infoG = makeSingleActivitySliceInfoGrid(
          state?.singleActivitySliceCashflow,
          state?.singleActivitySlice?.summary
        );
        return infoG;
      } catch (error) {
        console.error(
          `ERROR: makeSingleActivitySliceInfoGrid
          ${error}
          `
        );
        return;
      }
    },
    singleDraftActivitySliceInfoGrid(state) {
      try {
        return makeSingleDraftActivitySliceInfoGrid(
          state?.singleDraftActivitySliceCashflow,
          state?.singleDraftActivitySlice?.summary
        );
      } catch (error) {
        console.error(
          `ERROR: makeSingleDraftActivitySliceInfoGrid
          ${error}
          `
        );
      }
    },
    campaignProductsList(state) {
      return state?.campaignProductsList;
    },
    sliceProductsList(state) {
      return state?.sliceProductsList;
    },
    sliceInputCostPeriods(state) {
      return state.sliceInputCostPeriods;
    },
    activities(state) {
      return state.activities;
    },
    species(state) {
      return state.species;
    },
    singleAbstractCampaign(state) {
      return state.singleAbstractCampaign;
    },
    abstractCampaigns(state) {
      return state.abstractCampaigns;
    },
    campaigns(state) {
      return state.campaigns;
    },
    singleCampaign(state) {
      return state.singleCampaign;
    },
    singleEngagement(state) {
      return state.singleEngagement;
    },
    singleDraftEngagement(state) {
      return state.singleDraftEngagement;
    },
    singleDraftEngagementMetrics(state) {
      return state.singleDraftEngagementMetrics;
    },
    singleEngagementMetrics(state) {
      return state.singleEngagementMetrics;
    },
    directSliceCostItems(state) {
      return state.directSliceCostItems;
    },

    costItemFamilies(state) {
      return state.costItemFamilies;
    },
    costItemGroups(state) {
      return state.costItemGroups;
    },
    costItemGroupElements(state) {
      return state.costItemGroupElements;
    },
    variableShareCropSchedule(state) {
      return state.singleActivitySlice.variableShareCropSchedule;
    },
    draftVariableShareCropSchedule(state) {
      return state.singleDraftActivitySlice.variableShareCropSchedule;
    },
    financialPolicies(state) {
      return state.financialPolicies;
    },
    interestRates(state) {
      return state.interestRates;
    },
    activitySlicesEconomicCashflow(state) {
      return state.activitySlicesEconomicCashflow;
    },
    engagementCashflow(state) {
      return state.engagementCashflow;
    },
    campaignCashflow(state) {
      return state.campaignCashflow;
    },
    campaignMetrics(state) {
      return state.campaignMetrics;
    },
    campaignAdjustedMaxInv(state) {
      return state.campaignAdjustedMaxInv;
    },
    drilledDownCampaignMetrics(state) {
      return state.drilledDownCampaignMetrics;
    },
    campaignSimulationMetrics(state) {
      return state.campaignSimulationMetrics;
    },
    campaignSimulationT3Table(state) {
      return state.campaignSimulationT3Table;
    },
    draftEngagementCashflow(state) {
      return state.draftEngagementCashflow;
    },
    engagementResourceUtilization(state) {
      return state.engagementResourceUtilization;
    },
    draftEngagementResourceUtilization(state) {
      return state.draftEngagementResourceUtilization;
    },
    campaignResourceUtilization(state) {
      return state.campaignResourceUtilization;
    },
    cartesianProductTerms(state) {
      return state.cartesianProductTerms;
    },
    homepageMetrics(state) {
      return state.homepageMetrics;
    },
    apiKeys: (state) => {
      return state.apiKeys;
    },
    singleActivity: (state) => {
      return state.singleActivity;
    },
    singleSpecies: (state) => {
      return state.singleSpecies;
    },
    costInputPeriods: (state) => {
      return state.costInputPeriods;
    },
    activitySliceOutOfRangeAnalysis: (state) => {
      return state.activitySliceOutOfRangeAnalysis || [];
    },
    draftActivitySliceOutOfRangeAnalysis: (state) => {
      return state.draftActivitySliceOutOfRangeAnalysis || [];
    },
    activityTotalHaToolTip() {
      return activityLevelTotalHaToolTip;
    },
  },
  mutations: {
    SET_METRICS_FONT_SIZE(state, size) {
      const valid = size === "S" || size === "M" || size === "L";
      const next = valid ? size : "M";
      state.metricsFontSize = next;
      localStorage.setItem("tamanoDeLetraMetrics", next);
    },
    UPDATE_LOADING_STATUS(state, loadingStatus) {
      /* 
      loadingQueueStatus==0 -> not loading
      loadingQueueStatus>=1  -> loading 
      */
      // Clamp at zero so unbalanced "false" dispatches can't drive the
      // counter negative and desync the spinner from in-flight requests
      state.loadingQueueStatus = Math.max(
        0,
        state.loadingQueueStatus + loadingStatus,
      );
      state.isPageLoading = state.loadingQueueStatus > 0;
    },
    SET_ALL_MANAGERS(state, managers) {
      state.managers = managers;
    },
    SET_ALL_DIRECTORS(state, directors) {
      state.directors = directors;
    },
    SET_ALL_USERS(state, users) {
      state.allUsers = users;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_USER_IS_LOGG(state, isUserLog) {
      state.isUserLog = isUserLog;
    },
    SET_USER_TOKEN_INFO(state, userTokenInfo) {
      state.userTokenInfo = userTokenInfo;
    },
    SET_CAMPAIGN_ACTIVITIES(state, activities) {
      state.campaignActivities = activities;
    },
    SET_CAMPAIGN_ACTIVITY_SLICES(state, activitySlices) {
      state.campaignActivitySlices = activitySlices;
    },
    SET_AGG_ENGAGEMENT_T3_METRICS(state, aggEngagementT3Metrics) {
      state.aggEngagementT3Metrics = aggEngagementT3Metrics;
    },
    SET_AGG_DRAFT_ENGAGEMENT_T3_METRICS(state, aggDraftEngagementT3Metrics) {
      state.aggDraftEngagementT3Metrics = aggDraftEngagementT3Metrics;
    },
    SET_ENGAGEMENT_ACTIVITY_SLICES(state, activitySlices) {
      state.engagementActivitySlices = activitySlices;
    },
    SET_ENGAGEMENT_T3_METRICS_DATA(state, t3MetricsData) {
      state.engagementT3MetricsData = t3MetricsData;
    },
    SET_ACTIVITY_PRICES_FROM_CAMPAIGN(state, activityPrices) {
      state.activityPricesFromCampaign = activityPrices;
    },
    SET_ENGAGEMENTS(state, engagements) {
      state.engagements = engagements;
    },
    SET_ENGAGEMENT_SUMMARY(state, engagementSummary) {
      state.singleEngagementSummary = engagementSummary;
    },
    TOGGLE_SIDEBAR_DRAWER(state, value) {
      if (value !== undefined) {
        state.sideBarDrawer = value;
      } else {
        state.sideBarDrawer = !state.sideBarDrawer;
      }
    },
    SET_VIEWS(state, views) {
      state.views = alphabetized(views);
    },
    SET_VIEW_OBJECT(state, viewObject) {
      state.viewObject = viewObject;
    },
    ADD_NOTIFICATION_TO_QUEUE(state, notification) {
      console.log("Notifications!", state.notificationsQueue);
      if (state.notificationsQueue.length == 5) {
        console.log(
          "Max notifications reach!",
          state.notificationsQueue.length
        );
        state.notificationsQueue.pop();
        console.log("Notifications fix!", state.notificationsQueue.length);
      }
      state.notificationsQueue.unshift(notification);
      console.log("Notifications!", state.notificationsQueue);
    },
    DELETE_NOTIFICATION_FROM_QUEUE(state, id) {
      console.log("removing notification with id ", id);
      state.notificationsQueue = state.notificationsQueue.filter(
        (el) => el.id != id
      );
      console.log(state.notificationsQueue);
    },
    SET_SUMMARY_BY_ACTIVITY(state, viewObject) {
      state.summary_by_activity = viewObject;
    },
    SET_SUMMARY_BY_REGION(state, viewObject) {
      state.summary_by_region = viewObject;
    },
    SET_SIM_FACTORS(state, simFactors) {
      state.simFactors = simFactors;
    },
    SET_FORWARD_PRICES(state, forwardPrices) {
      state.forwardPrices = forwardPrices;
    },
    SET_ACTIVITY_SLICES(state, activitySlices) {
      state.activitySlices = activitySlices;
    },
    SET_DRAFT_ACTIVITY_SLICES(state, draftActivitySlices) {
      state.draftActivitySlices = draftActivitySlices;
    },
    SET_DRAFT_ENGAGEMENT_ACTIVITY_SLICES(state, draftActivitySlices) {
      state.draftEngagementActivitySlices = draftActivitySlices;
    },
    SET_DRAFT_ENGAGEMENT_T3_METRICS_DATA(state, t3MetricsData) {
      state.draftEngagementT3MetricsData = t3MetricsData;
    },
    SET_DRAFT_ENGAGEMENTS(state, draftEngagements) {
      state.draftEngagements = draftEngagements;
    },
    SET_DRAFT_ENGAGEMENTS_ATTACHMENT_ANALYSIS(state, data) {
      // If data has rows and keys, convert to array of objects
      if (data.rows && data.keys) {
        state.draftEngagementsAttachmentAnalysis = kRowsToItems(
          data.rows,
          data.keys
        );
      } else {
        state.draftEngagementsAttachmentAnalysis = data;
      }
    },
    CLEAR_DRAFT_ENGAGEMENTS(state) {
      state.draftEngagements = { key: [], items: [] };
    },
    CLEAR_DRAFT_ENGAGEMENT(state) {
      state.draftEngagement = {};
      state.draftEngagementSummary = { key: [], items: [] };
    },
    CLEAR_ENGAGEMENTS(state) {
      state.engagements = { key: [], items: [] };
    },
    CLEAR_ENGAGEMENT(state) {
      state.engagement = {};
      state.engagementSummary = { key: [], items: [] };
    },
    SET_CAMPAIGN_ENGAGEMENTS(state, campaignEngagements) {
      state.campaignEngagements = campaignEngagements;
    },
    SET_SINGLE_ACTIVITY_SLICE(state, singleActivitySlice) {
      state.singleActivitySlice = singleActivitySlice;
    },
    SET_SINGLE_DRAFT_ACTIVITY_SLICE(state, singleDraftActivitySlice) {
      state.singleDraftActivitySlice = singleDraftActivitySlice;
      // Keep the stale-while-revalidate summary cache fresh on every path
      // that updates the summary (initial load, row-save refreshes, etc.).
      writeCachedDraftSummary(
        singleDraftActivitySlice?.id,
        singleDraftActivitySlice?.summary
      );
    },
    SET_DRAFT_ACTIVITY_SLICE_CASHFLOW(state, cashflow) {
      state.singleDraftActivitySliceCashflow = cashflow;
    },
    CLEAR_SINGLE_ACTIVITY_SLICE(state) {
      state.singleActivitySlice = {};
    },
    CLEAR_ACTIVITY_SLICES(state) {
      state.activitySlices = [];
    },
    CLEAR_DRAFT_ACTIVITY_SLICES(state) {
      state.draftActivitySlices = [];
    },
    CLEAR_SINGLE_DRAFT_ACTIVITY_SLICE(state) {
      state.singleDraftActivitySlice = {};
    },
    SET_CAMPAIGN_PRODUCTS_LIST(state, campaignProductsList) {
      state.campaignProductsList = campaignProductsList;
    },
    SET_CAMPAIGN_MODIFIERS(state, mods) {
      state.campaignModifiers = mods;
    },
    SET_FILTERED_CAMPAIGN_MODIFIERS(state, mods) {
      state.filteredCampaignModifiers = mods;
    },
    SET_YIELD_MODIFIERS(state, mods) {
      state.yieldModifiers = mods;
    },
    SET_PRICE_MODIFIERS(state, mods) {
      state.priceModifiers = mods;
    },
    SET_COST_ITEM_FAMILY_MODIFIERS(state, mods) {
      state.costItemFamilyModifiers = mods;
    },
    SET_FREIGHT_MODIFIERS(state, mods) {
      state.freightModifiers = mods;
    },
    SET_RENT_MODIFIERS(state, mods) {
      state.rentModifiers = mods;
    },
    SET_SLICE_PRODUCTS_LIST(state, sliceProductsList) {
      state.sliceProductsList = sliceProductsList;
    },
    SET_SLICE_COST_INPUT_PERIODS(state, sliceInputCostPeriods) {
      state.sliceInputCostPeriods = sliceInputCostPeriods;
    },
    SET_ESTABLISHMENTS(state, establishments) {
      state.establishments = alphabetized(establishments, "name");
    },
    SET_SINGLE_ESTABLISHMENT(state, singleEstablishment) {
      state.singleEstablishment = singleEstablishment;
    },
    SET_REGIONS(state, regions) {
      state.regions = alphabetized(regions, "name");
    },
    SET_ACTIVITIES(state, activities) {
      state.activities = alphabetized(activities, "name");
    },
    SET_SPECIES(state, species) {
      state.species = alphabetized(species, "name");
    },
    SET_SINGLE_ABSTRACT_CAMPAIGN(state, singleAbstractCampaign) {
      state.singleAbstractCampaign = singleAbstractCampaign;
    },
    SET_ABSTRACT_CAMPAIGNS(state, abstractCampaigns) {
      state.abstractCampaigns = alphabetized(abstractCampaigns, "name");
    },
    SET_CAMPAIGNS(state, campaigns) {
      state.campaigns = campaigns;
    },
    SET_SINGLE_CAMPAIGN(state, singleCampaign) {
      state.singleCampaign = singleCampaign;
    },
    SET_SINGLE_ENGAGEMENT(state, singleEngagement) {
      state.singleEngagement = singleEngagement;
    },
    SET_SINGLE_ENGAGEMENT_LINEAGE(state, singleEngagementLineage) {
      state.singleEngagementLineage = singleEngagementLineage;
    },
    SET_SINGLE_DRAFT_ENGAGEMENT(state, singleDraftEngagement) {
      state.singleDraftEngagement = singleDraftEngagement;
    },
    SET_DRAFT_ENGAGEMENT_METRICS(state, metrics) {
      state.singleDraftEngagementMetrics = metrics;
    },
    SET_ENGAGEMENT_METRICS(state, metrics) {
      state.singleEngagementMetrics = metrics;
    },
    SET_DIRECT_SLICE_COST_ITEMS(state, items) {
      state.directSliceCostItems = alphabetized(items, "name");
    },
    SET_COST_ITEM_FAMILIES(state, families) {
      state.costItemFamilies = families;
    },
    SET_COST_ITEM_GROUPS(state, groups) {
      state.costItemGroups = groups;
    },
    SET_SINGLE_COST_GROUP_ELEMENTS(state, elements) {
      state.costItemGroupElements = elements;
    },
    SET_FINANCIAL_POLICIES(state, policies) {
      state.financialPolicies = alphabetized(policies, "name");
    },
    SET_INTEREST_RATES(state, rates) {
      state.interestRates = rates;
    },
    SET_OTHER_TON_COST_ITEMS(state, items) {
      state.otherTonCostItems = alphabetized(items, "name");
    },
    SET_PAGE_LOADING(state, status) {
      state.isPageLoading = status;
    },
    SET_ACTIVITY_SLICES_ECONOMIC_CASHFLOWS(state, cashflows) {
      state.activitySlicesEconomicCashflow = cashflows;
    },
    SET_ACTIVITY_SLICE_CASHFLOW(state, cashflow) {
      state.singleActivitySliceCashflow = cashflow;
    },
    SET_ENGAGEMENT_CASHFLOW(state, cashflow) {
      state.engagementCashflow = cashflow;
    },
    SET_CAMPAIGN_CASHFLOW(state, cashflow) {
      state.campaignCashflow = cashflow;
    },
    SET_CAMPAIGN_METRICS(state, metrics) {
      state.campaignMetrics = metrics;
    },
    SET_CAMPAIGN_ADJUSTED_MAX_INV(state, record) {
      state.campaignAdjustedMaxInv = record;
    },
    SET_DRILLED_DOWN_CAMPAIGN_METRICS(state, metrics) {
      state.drilledDownCampaignMetrics = metrics;
    },
    SET_CAMPAIGN_SIMULATION_METRICS(state, metrics) {
      state.campaignSimulationMetrics = metrics;
    },
    SET_CAMPAIGN_SIMULATION_T3_TABLE(state, t3Table) {
      state.campaignSimulationT3Table = t3Table;
    },
    SET_DRAFT_ENGAGEMENT_CASHFLOW(state, cashflow) {
      state.draftEngagementCashflow = cashflow;
    },
    SET_ENGAGEMENT_RESOURCE_UTILIZATION(state, resourceUtilization) {
      state.engagementResourceUtilization = resourceUtilization;
    },
    SET_DRAFT_ENGAGEMENT_RESOURCE_UTILIZATION(state, resourceUtilization) {
      state.draftEngagementResourceUtilization = resourceUtilization;
    },
    SET_CAMPAIGN_RESOURCE_UTILIZATION(state, resourceUtilization) {
      state.campaignResourceUtilization = resourceUtilization;
    },
    SET_DRAFT_ENGAGEMENTS_TO_COMPARE(state, draftEngagements) {
      state.draftEngagementsToCompare = draftEngagements;
    },
    SET_CAMPAIGN_VERSIONS_TO_COMPARE(state, campaignVersions) {
      state.campaignVersionsToCompare = campaignVersions;
    },
    SET_IS_DRAFT_ENGAGEMENT_COMPARE_ACTIVE(
      state,
      isDraftEngagementCompareActive
    ) {
      state.isDraftEngagementCompareActive = isDraftEngagementCompareActive;
    },
    CAMPAIGN_(state, t1MetricsComparison) {
      state.t1MetricsComparison = t1MetricsComparison;
    },
    SET_T4_METRICS_COMPARISON(state, t4MetricsComparison) {
      state.t4MetricsComparison = t4MetricsComparison;
    },
    SET_T3_METRICS_COMPARISON(state, t3MetricsComparison) {
      state.t3MetricsComparison = t3MetricsComparison;
    },
    SET_T1_METRICS_CAMPAIGN_COMPARISON(state, t1MetricsCampaignComparison) {
      state.t1MetricsCampaignComparison = t1MetricsCampaignComparison;
    },
    SET_T4_METRICS_CAMPAIGN_COMPARISON(state, t4MetricsCampaignComparison) {
      state.t4MetricsCampaignComparison = t4MetricsCampaignComparison;
    },
    SET_T3_METRICS_CAMPAIGN_COMPARISON(state, t3MetricsCampaignComparison) {
      state.t3MetricsCampaignComparison = t3MetricsCampaignComparison;
    },
    SET_CARTESIAN_PRODUCT_TERMS(state, cartesianProductTerms) {
      state.cartesianProductTerms = cartesianProductTerms;
    },
    SET_HOMEPAGE_METRICS(state, metrics) {
      state.homepageMetrics = metrics;
    },
    SET_API_KEYS(state, apiKeys) {
      state.apiKeys = apiKeys;
    },
    REMOVE_API_KEY(state, apiKeyId) {
      state.apiKeys = state.apiKeys.filter((key) => key.id !== apiKeyId);
    },
    SET_SINGLE_ACTIVITY(state, activity) {
      state.singleActivity = activity;
    },
    SET_SINGLE_SPECIES(state, species) {
      state.singleSpecies = species;
    },
    SET_COST_INPUT_PERIODS(state, periods) {
      state.costInputPeriods = periods;
    },
    SET_ACTIVITY_SLICE_OUT_OF_RANGE_ANALYSIS(state, analysis) {
      state.activitySliceOutOfRangeAnalysis = analysis;
    },
    SET_DRAFT_ACTIVITY_SLICE_OUT_OF_RANGE_ANALYSIS(state, analysis) {
      state.draftActivitySliceOutOfRangeAnalysis = analysis;
    },
  },
  actions: {
    setPageLoadingStatus({ commit }, status) {
      if (status === false) {
        commit("UPDATE_LOADING_STATUS", -1);
      } else if (status === true) {
        commit("UPDATE_LOADING_STATUS", +1);
      }
    },
    getAllUsers({ dispatch }) {
      dispatch("getAllManagers");
      dispatch("getAllDirectors");
    },
    async getAllDirectors({ commit, dispatch }) {
      let endpoint = "/user/director";
      let directorsResult = await getRequest(endpoint);
      if (directorsResult?.success) {
        commit("SET_ALL_DIRECTORS", directorsResult?.data);
      } else {
        dispatch("setNewNotification", directorsResult);
      }
    },
    async getAllManagers({ commit, dispatch }) {
      let endpoint = "/user/manager";
      let managersResult = await getRequest(endpoint);
      if (managersResult?.success) {
        let managers = managersResult?.data;
        let establishmentEndpoint = "/manager_to_establishment";
        let establishmentListResult = [];

        if (managers?.length > 0) {
          for (const manager of managers) {
            let filter = `?q=manager_id:${manager.id}`;
            establishmentListResult.push(
              getRequest(establishmentEndpoint + filter)
            );
          }
          establishmentListResult = await Promise.all(establishmentListResult);
          let i = 0;
          for (const manager of managers) {
            manager.establishments = [];
            if (establishmentListResult?.[i]?.success) {
              let establishmentsRelations = establishmentListResult?.[i]?.data;
              manager.establishmentsIds = establishmentsRelations.map(
                (el) => el.establishmentId
              );
              manager.establishmentsRelations = establishmentsRelations;
            } else {
              dispatch("setNewNotification", establishmentListResult?.[i]);
            }
            i++;
          }
        }
        commit("SET_ALL_MANAGERS", managers);
      } else {
        dispatch("setNewNotification", managersResult);
      }
    },
    setUserTokenInfo({ commit }, decodedToken) {
      commit("SET_USER_IS_LOGG", true);
      commit("SET_USER_TOKEN_INFO", decodedToken);

      // Store the user type in localStorage for persistence across page refreshes
      if (decodedToken?.utype) {
        localStorage.setItem("userType", decodedToken.utype);
      }
    },
    unsetUserTokenInfo({ commit }) {
      commit("SET_USER_IS_LOGG", false);
      commit("SET_USER_TOKEN_INFO", {});

      // Clear the stored user type
      localStorage.removeItem("userType");
      console.log("User token info cleared");
    },
    async getActivityPricesFromCampaign({ commit, dispatch }, campaign_id) {
      let activityPrices = [];
      let endpoint = "/views/campaign_prices_by_activity";
      let filter = `?q=campaign_id:${campaign_id}`;
      let query = endpoint + filter;
      let response = await getRequest(query);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        activityPrices = kRowsToItems(
          response?.data?.rows,
          response?.data?.keys
        );
      }
      commit("SET_ACTIVITY_PRICES_FROM_CAMPAIGN", activityPrices);
    },
    setNotificationTimeOut({ dispatch }, payload) {
      let id = payload?.id ? payload.id : Date.now();
      // This will set a default timeout if no timeout is given
      let timeout = payload?.timeout ? payload.timeout : 5000;

      // Set notification timeout
      setTimeout(() => {
        dispatch("unSetNotification", id);
      }, timeout);
    },
    unSetNotification({ commit }, id) {
      commit("DELETE_NOTIFICATION_FROM_QUEUE", id);
    },
    setNewNotification({ commit, dispatch }, notification) {
      let notificationId = Date.now();
      let notif;
      let isNotificationParsed = checkNotificationStructure(notification);
      if (isNotificationParsed) {
        notif = notification.data;
        notif.id = notificationId;
        commit("ADD_NOTIFICATION_TO_QUEUE", notif);
      } else {
        console.log("bad notif", notification);
        notif = {
          id: notificationId,
          type: "alert",
          text: "Hubo un error con esta notificacion, no se pudo mostrar correctamente",
        };
        commit("ADD_NOTIFICATION_TO_QUEUE", notif);
      }
      let textCharacters = notif?.text.length;
      let infoCharacters = notif?.info?.reduce((acc, str) => {
        return acc + str.length;
      }, 0);
      // This will give a timeout of 1 sec every character
      let notificationTimeout = (textCharacters + infoCharacters) * 1000;
      dispatch("setNotificationTimeOut", {
        id: notificationId,
        timeout: notificationTimeout,
      });
    },
    setDraftEngagementCompareActive({ commit }) {
      commit("SET_IS_DRAFT_ENGAGEMENT_COMPARE_ACTIVE", true);
    },
    unSetDraftEngagementCompareActive({ commit }) {
      commit("SET_IS_DRAFT_ENGAGEMENT_COMPARE_ACTIVE", false);
    },
    setDraftEngagementsToCompare({ commit }, draftEngagements) {
      commit("SET_DRAFT_ENGAGEMENTS_TO_COMPARE", draftEngagements);
    },
    setCampaignVersionsToCompare({ commit }, campaignVersions) {
      commit("SET_CAMPAIGN_VERSIONS_TO_COMPARE", campaignVersions);
    },
    toggleSideBarDrawer({ commit }) {
      commit("TOGGLE_SIDEBAR_DRAWER");
    },

    async getMetricsForEngagementComparison(
      { commit, dispatch },
      draftEngagementIdArray
    ) {
      dispatch("setPageLoadingStatus", true);
      let endpoint = `/views/draft_activity_slice_summary?limit=10000`;
      let draftEngagementSummaryResponses = [];
      let draftEngagementsResponse = [];
      let t3MetricsComparison = {};
      for (const draftEngagementId of draftEngagementIdArray) {
        let summaryEndpoint =
          endpoint + `&q=draft_engagement_id:${draftEngagementId}`;
        draftEngagementSummaryResponses.push(getRequest(summaryEndpoint, true));
        draftEngagementsResponse.push(
          getRequest(`/draft_engagement/${draftEngagementId}`)
        );
      }

      let draftEngagementSummaryArray = await Promise.all(
        draftEngagementSummaryResponses
      );
      let draftEngagementsResult = await Promise.all(draftEngagementsResponse);

      // Initial values for years
      let startYear = 2049;
      let startMonth = 1;
      let endYear = 2001;
      let endMonth = 1;

      let i = 0;
      for (let j = 0; j < draftEngagementIdArray.length; j++) {
        if (!draftEngagementsResult?.[j].success) {
          dispatch("setNewNotification", draftEngagementsResult?.[j]);
        } else {
          let data = draftEngagementsResult?.[j]?.data;
          let date = new Date(data?.variableParticipationDate);
          date.setMonth(date.getMonth() - 18);
          if (startYear > date.getFullYear()) {
            startYear = date.getFullYear();
            startMonth = date.getMonth() + 1;
          }
          // Gives 18 months back
          date.setMonth(date.getMonth() + 18);
          // Add 1 Year more
          if (endYear < date.getFullYear() + 1) {
            endYear = date.getFullYear();
            endMonth = date.getMonth() + 1;
          }
        }
        if (!draftEngagementSummaryArray?.[j].success) {
          dispatch("setNewNotification", draftEngagementSummaryArray?.[j]);
        } else {
          let data = draftEngagementSummaryArray?.[j]?.data;
          let keys = data.keys;
          let items = kRowsToItems(data.rows, keys);
          items = items.map((item) => {
            return {
              ...item,
              totalRevenue: item?.totalRevenue?.toLocaleString("en-US", {
                timeZone: "America/Argentina/Buenos_Aires",
              }),
            };
          });
          t3MetricsComparison[draftEngagementIdArray[i]] = items;
          i++;
        }
      }

      endpoint = `/module/get/metrics/draft-engagement/compare/${draftEngagementIdArray[0]}/${draftEngagementIdArray[1]}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let t1MetricsComparison = response?.data?.t1;
        let t4MetricsComparison = response?.data?.t4;

        commit("CAMPAIGN_", t1MetricsComparison);
        commit("SET_T4_METRICS_COMPARISON", t4MetricsComparison);
        commit("SET_T3_METRICS_COMPARISON", t3MetricsComparison);
      }
      //
      dispatch("setPageLoadingStatus", false);
    },
    async getMetricsForCampaignVersionComparison({ commit, dispatch }, ids) {
      dispatch("setPageLoadingStatus", true);
      let startYear;
      let startMonth;
      let endYear;
      let endMonth;
      let campaignVersionsIdArray = ids.campaignVersionsIdArray;
      let abstractCampaignId = ids.abstractCampaignId;
      let endpoint = `/views/campaign_activity_slice_summary?limit=10000`;
      let campaignVersionSummaryResponses = [];

      let t3MetricsComparison = {};
      let abstractCampaignResult = await getRequest(
        `/abstract_campaign/${abstractCampaignId}`
      );
      if (!abstractCampaignResult.success) {
        dispatch("setNewNotification", abstractCampaignResult);
      } else {
        let data = abstractCampaignResult?.data;
        let startDate = new Date(data?.startDate);
        let endDate = new Date(data?.endDate);
        startYear = startDate.getFullYear();
        startMonth = startDate.getMonth() + 1;
        endYear = endDate.getFullYear();
        endMonth = endDate.getMonth() + 1;
      }

      for (const campaignVersionId of campaignVersionsIdArray) {
        let summaryEndpoint = endpoint + `&q=campaign_id:${campaignVersionId}`;
        campaignVersionSummaryResponses.push(getRequest(summaryEndpoint, true));
      }

      let campaignVersionSummaryArray = await Promise.all(
        campaignVersionSummaryResponses
      );

      // Initial values for years

      let i = 0;
      for (let j = 0; j < campaignVersionsIdArray.length; j++) {
        if (!campaignVersionSummaryArray?.[j].success) {
          dispatch("setNewNotification", campaignVersionSummaryArray?.[j]);
        } else {
          let data = campaignVersionSummaryArray?.[j]?.data;

          let keys = data.keys;
          let items = kRowsToItems(data.rows, keys);
          items = items.map((item) => {
            return {
              ...item,
              totalRevenue: parseToLocaleString(item?.totalRevenue),
            };
          });
          t3MetricsComparison[campaignVersionsIdArray[i]] = items;
          i++;
        }
      }

      endpoint = `/module/get/metrics/campaign/compare/${campaignVersionsIdArray[0]}/${campaignVersionsIdArray[1]}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let t1MetricsComparison = response?.data?.t1;
        let t4MetricsComparison = response?.data?.t4;
        console.log(t1MetricsComparison);

        commit("SET_T1_METRICS_CAMPAIGN_COMPARISON", t1MetricsComparison);
        commit("SET_T4_METRICS_CAMPAIGN_COMPARISON", t4MetricsComparison);
        commit("SET_T3_METRICS_CAMPAIGN_COMPARISON", t3MetricsComparison);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getHomepageMetrics({ commit, dispatch }) {
      let endpoint = `/module/get/metrics/home`;
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
        return;
      }

      // The metrics already include activities and species counts
      commit("SET_HOMEPAGE_METRICS", response.data);
    },
    async getSingleEngagementSummary({ commit, dispatch }, engagementId) {
      let endpoint = `/module/get/polars-views/enriched_campaign_engagements?q=engagement_id:${engagementId}`;
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        // New Polars API returns data as array of objects in response.data.data
        let summaries = response.data.data || [];
        
        // Convert snake_case to camelCase
        summaries = keysToCamel(summaries);

        if (summaries.length !== 1) {
          let notification = {
            data: {
              type: "alert",
              text: `${endpoint} failed! Length != 0. ${JSON.stringify(
                summaries
              )}`,
            },
          };
          dispatch("setNewNotification", notification);
        }
        let summary = summaries[0];
        commit("SET_ENGAGEMENT_SUMMARY", summary);
      }
    },
    clearSingleDraftEngagementMetrics({ commit }) {
      commit("SET_DRAFT_ENGAGEMENT_METRICS", {});
    },
    async getSingleDraftEngagementMetrics({ commit, dispatch }, payload) {
      let id = payload.id;
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let endpoint = `/module/get/metrics/draft-engagement/${id}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let metrics = {};
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        metrics = response?.data;
        commit("SET_DRAFT_ENGAGEMENT_METRICS", metrics);
      }
    },
    async getSingleEngagementMetrics({ commit, dispatch }, payload) {
      let id = payload.id;
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let endpoint = `/module/get/metrics/engagement/${id}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let metrics = response.data;
        commit("SET_ENGAGEMENT_METRICS", metrics);
      }
    },
    async getAvailableViews({ commit, dispatch, state }) {
      state.availableViewsLoading = true;
      let response = await getRequest("/views", true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let views = response.data.views;
        commit("SET_VIEWS", views);
      }
      state.availableViewsLoading = false;
    },
    async getDbView({ commit, dispatch, state }, payload) {
      state.viewObject = {};
      let offset = (payload.page - 1) * 100;
      let view = payload.view;
      let limit = payload.limit;
      state.viewObjectLoading = true;
      let response = await getRequest(
        "/views/" + view + "?limit=" + limit + "&offset=" + offset,
        true
      );
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let viewObject = response.data;

        viewObject = processViewObject(viewObject);

        commit("SET_VIEW_OBJECT", viewObject);
        state.viewObjectLoading = false;
      }
    },
    async getSummaries({ commit, dispatch, state }) {
      state.summariesLoading = true;
      let r1 = await getRequest("/views/enriched_summary_by_activity", true);
      let r2 = await getRequest("/views/enriched_summary_by_region", true);
      let r3 = await getRequest("/views/ra_simulation", true);
      let r4 = await getRequest("/activity", false);
      let responsesHasError = false;
      for (const response of [r1, r2, r3, r4]) {
        if (!response.success) {
          dispatch("setNewNotification", response);
          responsesHasError = true;
        }
      }
      if (!responsesHasError) {
        let byRegion = r2.data;
        let byActivity = r1.data;
        let simFactors = r3.data;
        let forwardPrices = r4.data;
        let fpricekeys = Object.keys(forwardPrices[0]);

        let forwardPricesObject = {
          rows: forwardPrices.map((K) => {
            let res = [];
            for (let i = 0; i < fpricekeys.length; i++) {
              const element = K[fpricekeys[i]];
              res.push(element);
            }
            return res;
          }),
          keys: fpricekeys,
        };

        commit("SET_SUMMARY_BY_ACTIVITY", processViewObject(byActivity));
        commit("SET_SUMMARY_BY_REGION", processViewObject(byRegion));
        commit("SET_SIM_FACTORS", simFactors);
        commit("SET_FORWARD_PRICES", forwardPricesObject);
      }
      state.summariesLoading = false;
    },
    async updateFactor({ dispatch, state }, payload) {
      state.summariesLoading = true;

      let id = payload.id;
      let factor = payload.factor;
      let response = await putRequest("/region_activity_simulation/" + id, {
        factor,
      });

      if (!response.success) {
        this.$store.dispatch("setNewNotification", response);
      } else {
        dispatch("getSummaries");
      }
    },
    async updateForwardPrice({ dispatch, state }, payload) {
      state.summariesLoading = true;
      let id = payload.id;
      let forwardPrice = payload.forwardPrice;

      let response = await putRequest("/activity/" + id, {
        forwardPrice,
      });

      if (!response.success) {
        this.$store.dispatch("setNewNotification", response);
      } else {
        dispatch("getSummaries");
      }
    },
    async getSingleDraftActivitySlice(
      { commit, dispatch, state },
      draftActivitySliceId
    ) {
      dispatch("setPageLoadingStatus", true);
      try {
        let response = await getDraftASByID(draftActivitySliceId);

        if (!response.success) {
          if (response.isArray) {
            let arrayResponse = response.data;
            for (const resp of arrayResponse) {
              dispatch("setNewNotification", resp);
            }
          } else {
            dispatch("setNewNotification", response);
          }
        } else {
          let draftActivitySlice = response.data;

          // Stale-while-revalidate: render the last known summary right away;
          // the fresh one replaces it below when the slow query resolves.
          let cachedSummary = readCachedDraftSummary(draftActivitySlice.id);
          if (cachedSummary) {
            draftActivitySlice = {
              ...draftActivitySlice,
              summary: cachedSummary,
            };
          }
          commit("SET_SINGLE_DRAFT_ACTIVITY_SLICE", draftActivitySlice);

          // The summary view is slow on the backend; merge it in when it
          // resolves instead of blocking the initial render on it.
          response.summaryPromise
            .then((summaryResult) => {
              if (!summaryResult.success) {
                dispatch("setNewNotification", summaryResult);
                return;
              }
              // Skip if the user navigated to another slice meanwhile.
              if (
                state.singleDraftActivitySlice?.id !== draftActivitySlice.id
              ) {
                return;
              }
              commit("SET_SINGLE_DRAFT_ACTIVITY_SLICE", {
                ...state.singleDraftActivitySlice,
                summary: summaryResult.data,
              });
            })
            .catch((error) => {
              dispatch("setNewNotification", {
                type: "error",
                text: `${error}`,
              });
            });
        }
      } catch (error) {
        let notif = { type: "error", text: `${error}` };
        dispatch("setNewNotification", notif);
        console.log(`Catching exception:`, error);
      }

      dispatch("setPageLoadingStatus", false);
    },
    async sortDraftActivitySliceCostItems({ commit, state }, sortKey) {
      let singleDraftActivitySlice = { ...state.singleDraftActivitySlice };
      singleDraftActivitySlice.assignedSCostItems =
        singleDraftActivitySlice.assignedSCostItems.sort(
          (a, b) => a[sortKey] - b[sortKey]
        );
      commit("SET_SINGLE_DRAFT_ACTIVITY_SLICE", singleDraftActivitySlice);
    },
    async getSingleActivitySlice({ commit, dispatch }, activitySliceId) {
      dispatch("setPageLoadingStatus", true);

      try {
        let activitySlice = await getASByID(activitySliceId);

        if (!activitySlice.success) {
          if (activitySlice.isArray) {
            let arrayResponse = activitySlice.data;
            for (const response of arrayResponse) {
              dispatch("setNewNotification", response);
            }
          } else {
            dispatch("setNewNotification", activitySlice);
          }
        } else {
          activitySlice = activitySlice.data;
          commit("SET_SINGLE_ACTIVITY_SLICE", activitySlice);
        }
      } catch (e) {
        let notif = { type: "error", text: `${e}` };
        dispatch("setNewNotification", notif);
        console.log(`Catching exception:`, e);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async clearSingleActivitySlice({ commit }) {
      commit("CLEAR_SINGLE_ACTIVITY_SLICE");
    },
    async clearActivitySlices({ commit }) {
      commit("CLEAR_ACTIVITY_SLICES");
    },
    async clearDraftActivitySlices({ commit }) {
      commit("CLEAR_DRAFT_ACTIVITY_SLICES");
    },
    async clearSingleDraftActivitySlice({ commit }) {
      commit("CLEAR_SINGLE_DRAFT_ACTIVITY_SLICE");
    },
    async clearDraftEngagements({ commit }) {
      commit("CLEAR_DRAFT_ENGAGEMENT");
    },
    async clearEngagements({ commit }) {
      commit("CLEAR_ENGAGEMENTS");
    },
    async clearEngagement({ commit }) {
      commit("CLEAR_ENGAGEMENT");
    },
    async getDraftActivitySliceCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let activitySliceId = payload?.activitySliceId;

      let endpoint = `/module/get/draft-cashflow/activity_slice/${activitySliceId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        masterCashflow = result.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow.rows);
        commit("SET_DRAFT_ACTIVITY_SLICE_CASHFLOW", masterCashflow);
      }
    },
    async getDraftEngagementCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let draftEngagementId = payload?.draftEngagementId;
      let endpoint = `/module/get/draft-cashflow/engagement/${draftEngagementId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        masterCashflow = result.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow.rows);
        commit("SET_DRAFT_ENGAGEMENT_CASHFLOW", masterCashflow);
      }
    },
    async getActivitySliceCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let activitySliceId = payload?.activitySliceId;

      let endpoint = `/module/get/project-cashflow/activity_slice/${activitySliceId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;
      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        masterCashflow = result.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow?.rows);
        commit("SET_ACTIVITY_SLICE_CASHFLOW", masterCashflow);
      }
    },
    async getEngagementCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let masterRU = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let engagementId = payload?.engagementId;
      let endpoint = `/module/get/project-cashflow/engagement/${engagementId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;

      // Get both cashflow and resource utilization in parallel
      let [cashflowResult, ruResult] = await Promise.all([
        getRequest(endpoint, true),
        retrieveMultiResourceUtilization(
          payload.activitySliceIds // We'll need to pass this from the component
        ),
      ]);

      if (!cashflowResult.success) {
        dispatch("setNewNotification", cashflowResult);
      } else {
        masterCashflow = cashflowResult.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow.rows);
        commit("SET_ENGAGEMENT_CASHFLOW", masterCashflow);
      }

      if (!ruResult.success) {
        dispatch("setNewNotification", ruResult);
      } else {
        masterRU = ruResult.data;
        commit("SET_ENGAGEMENT_RESOURCE_UTILIZATION", masterRU);
      }
    },
    async getCampaignCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/project-cashflow/campaign/${campaignId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        masterCashflow = result.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow.rows);
        commit("SET_CAMPAIGN_CASHFLOW", masterCashflow);
      }
    },
    async getCampaignMetrics({ commit, dispatch }, payload) {
      let metrics = {};
      let startYear = payload?.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload?.endYear;
      let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/metrics/campaign/${campaignId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1&t3_mode=excluded`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      // Load both regular metrics and T3 metrics in parallel
      const [metricsResult, t3Result] = await Promise.all([
        getRequest(endpoint, true),
        getRequest(`/module/get/t3/campaign/${campaignId}?${payload?.speciesId ? `&species_id=${payload?.speciesId}` : ''}${payload?.regionId ? `&region_id=${payload?.regionId}` : ''}${payload?.activityIds ? `&activity_ids=${payload?.activityIds}` : ''}`, true)
      ]);

      if (!metricsResult.success) {
        dispatch("setNewNotification", metricsResult);
      } else {
        metrics = metricsResult.data;
        
        // Add T3 metrics if the request was successful
        if (t3Result.success) {
          metrics.t3 = t3Result.data.t3_table;
        } else {
          dispatch("setNewNotification", t3Result);
        }
        
        commit("SET_CAMPAIGN_METRICS", metrics);
      }
    },

    async getCampaignAdjustedMaxInv({ commit, dispatch }, campaignId) {
      const result = await getRequest(
        `/campaign_adjusted_max_inv?q=campaign_id:${campaignId}`
      );
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_CAMPAIGN_ADJUSTED_MAX_INV", result.data?.[0] ?? null);
      }
    },

    async createCampaignAdjustedMaxInv({ dispatch }, payload) {
      const result = await postRequest("/campaign_adjusted_max_inv", payload);
      if (!result.success) {
        dispatch("setNewNotification", result);
      }
      return result;
    },

    async updateCampaignAdjustedMaxInv({ dispatch }, { id, adjustedMaxInv }) {
      const result = await putRequest(`/campaign_adjusted_max_inv/${id}`, {
        adjustedMaxInv,
      });
      if (!result.success) {
        dispatch("setNewNotification", result);
      }
      return result;
    },

    async deleteCampaignAdjustedMaxInv({ dispatch }, id) {
      const result = await deleteRequest(`/campaign_adjusted_max_inv/${id}`);
      if (!result.success) {
        dispatch("setNewNotification", result);
      }
      return result;
    },

    async getCampaignT3Metrics({ commit, dispatch, state }, payload) {
      let metrics = {};
      // let startYear = payload?.startYear;
      // let startMonth = payload.startMonth;
      // let endYear = payload?.endYear;
      // let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/t3/campaign/${campaignId}`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        // Update the t3 part of the metrics using the new endpoint structure
        const currentMetrics = state.campaignMetrics || {};
        metrics = {
          ...currentMetrics,
          t3: result.data.t3_table
        };
        commit("SET_CAMPAIGN_METRICS", metrics);
      }
    },

    async getDrilledDownCampaignMetrics({ commit, dispatch }, payload) {
      let campaignId = payload?.campaignId;
      let regionId = payload?.regionId;
      let speciesId = payload?.speciesId;
      let activityIds = payload?.activityIds;

      // Validate mutual exclusivity between speciesId and activityIds
      if (speciesId && activityIds && activityIds.length > 0) {
        dispatch("setNewNotification", {
          type: "error",
          text: "species_id and activity_ids cannot be used together"
        });
        return;
      }

      let endpoint = `/module/get/metrics/campaign/drilled_down/${campaignId}`;

      // Build query parameters if filters are provided
      const params = new URLSearchParams();
      if (regionId) params.append("region_id", regionId);
      if (speciesId) params.append("species_id", speciesId);
      if (activityIds && activityIds.length > 0) {
        // Handle activityIds as array - join with commas
        const activityIdsString = Array.isArray(activityIds) ? activityIds.join(",") : activityIds;
        params.append("activity_ids", activityIdsString);
      }

      // Append query string if parameters exist
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      let result = await getRequest(endpoint, false);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_DRILLED_DOWN_CAMPAIGN_METRICS", result.data);
      }
    },

    async clearCampaignMetricsAndCashflow({ commit }) {
      commit("SET_CAMPAIGN_CASHFLOW", {});
      commit("SET_CAMPAIGN_METRICS", {});
    },

    async clearEngagemetMetricsAndCashflow({ commit }) {
      commit("SET_CAMPAIGN_METRICS", {});
      commit("SET_ENGAGEMENT_METRICS", {});
      commit("SET_SINGLE_ENGAGEMENT_LINEAGE", {});
    },

    async getCampaignSimulationCashflow({ commit, dispatch }, payload) {
      let masterCashflow = { rows: [], keys: [] };
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/project-cashflow/campaign/${campaignId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1&use_modifiers=1`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        masterCashflow = result.data;
        masterCashflow.rows = formatRowsToLocaleString(masterCashflow.rows);
        commit("SET_CAMPAIGN_CASHFLOW", masterCashflow);
      }
    },
    async getCampaignSimulationMetrics({ commit, dispatch }, payload) {
      let metrics = {};
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/metrics/campaign/${campaignId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_gather=1&use_modifiers=1&t3_mode=excluded`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        metrics = result.data;
        commit("SET_CAMPAIGN_SIMULATION_METRICS", metrics);
      }
    },

    async getCampaignSimulationT3Table({ commit, dispatch }, payload) {
      let startYear = payload.startYear;
      let startMonth = payload.startMonth;
      let endYear = payload.endYear;
      let endMonth = payload.endMonth;

      let campaignId = payload?.campaignId;
      let endpoint = `/module/get/t3/campaign/${campaignId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}&use_modifiers=1`;

      if (payload?.speciesId) {
        endpoint += `&species_id=${payload?.speciesId}`;
      }
      if (payload?.regionId) {
        endpoint += `&region_id=${payload?.regionId}`;
      }
      if (payload?.activityIds) {
        endpoint += `&activity_ids=${payload?.activityIds}`;
      }

      let result = await getRequest(endpoint, true);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_CAMPAIGN_SIMULATION_T3_TABLE", result.data);
      }
    },

    async getYieldModifiersByCampaignId({ dispatch, commit }, campaignId) {
      let endpoint = `/simulation_modifier_yield?q=campaign_id:${campaignId}&limit=10000`;
      let result = await getRequest(endpoint);
      if (!result?.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_YIELD_MODIFIERS", result.data);
      }
    },
    async getPriceModifiersByCampaignId({ dispatch, commit }, campaignId) {
      let endpoint = `/simulation_modifier_price?q=campaign_id:${campaignId}&limit=10000`;
      let result = await getRequest(endpoint);
      if (!result?.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_PRICE_MODIFIERS", result.data);
      }
    },
    async getCostItemFamilyModifiersByCampaignId(
      { dispatch, commit },
      campaignId
    ) {
      let endpoint = `/simulation_modifier_cost_item_family?q=campaign_id:${campaignId}&limit=10000`;
      let result = await getRequest(endpoint);
      if (!result?.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_COST_ITEM_FAMILY_MODIFIERS", result.data);
      }
    },
    async getFreightModifiersByCampaignId({ dispatch, commit }, campaignId) {
      let endpoint = `/simulation_modifier_freight?q=campaign_id:${campaignId}&limit=10000`;
      let result = await getRequest(endpoint);
      if (!result?.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_FREIGHT_MODIFIERS", result.data);
      }
    },
    async getRentModifiersByCampaignId({ dispatch, commit }, campaignId) {
      let endpoint = `/simulation_modifier_rent?q=campaign_id:${campaignId}&limit=10000`;
      let result = await getRequest(endpoint);
      if (!result?.success) {
        dispatch("setNewNotification", result);
      } else {
        commit("SET_RENT_MODIFIERS", result.data);
      }
    },
    async getCampaignActivities({ commit, dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);

      let filterKey = payload?.filterKey;
      let filterValue = payload?.filterValue;
      
      // Extract campaign_id and other filter params
      let campaignId = null;
      let queryParams = {};
      
      if (filterKey && filterValue) {
        // Check if filterKey contains specific parameters
        if (filterKey === 'campaign_id') {
          campaignId = filterValue;
        } else if (filterKey === 'region_id') {
          queryParams.region_id = filterValue;
        } else if (filterKey === 'activity_id') {
          queryParams.activity_id = filterValue;
        } else if (filterKey === 'species_id') {
          queryParams.species_id = filterValue;
        } else {
          // For other filters, use the old query format
          queryParams.q = `${filterKey}:${filterValue}`;
        }
      }
      
      // Build endpoints with new pattern
      let endpoint1, endpoint2;
      
      if (campaignId) {
        // Use new endpoint pattern with campaign_id in path
        const queryString = Object.keys(queryParams).length > 0 
          ? '?' + Object.entries(queryParams).map(([k, v]) => `${k}=${v}`).join('&')
          : '';
        
        endpoint1 = `/module/get/campaign-activity-slice-summary/campaign/${campaignId}${queryString}`;
        endpoint2 = `/module/get/activity-slice-revenues/campaign/${campaignId}${queryString}`;
      } else {
        // Fallback to views endpoint if no campaign_id
        const queryString = Object.keys(queryParams).length > 0 
          ? '&' + Object.entries(queryParams).map(([k, v]) => `${k}=${v}`).join('&')
          : '';
        
        endpoint1 = `/views/campaign_activity_slice_summary?limit=10000${queryString}`;
        endpoint2 = `/views/activity_slice_revenues?limit=10000${queryString}`;
      }

      let [response1, response2] = await Promise.all([
        getRequest(endpoint1, true),
        getRequest(endpoint2, true),
      ]);

      if (!response1.success) {
        dispatch("setNewNotification", response1);
      } else if (!response2.success) {
        dispatch("setNewNotification", response2);
      } else {
        let data1 = response1.data;
        let data2 = response2.data;
        
        // Handle different response formats
        let items1, items2;
        
        if (data1 && data1.keys && data1.rows) {
          // Old format with keys and rows
          let keys1 = data1.keys;
          let rows1 = formatRowsToLocaleString(data1.rows);
          items1 = kRowsToItems(rows1, keys1);
        } else if (data1 && data1.campaign_activity_slice_summary) {
          // New format - object with campaign_activity_slice_summary key containing array
          items1 = data1.campaign_activity_slice_summary;
        } else if (Array.isArray(data1)) {
          // Fallback - data is already an array
          items1 = data1;
        } else {
          items1 = [];
        }
        
        if (data2 && data2.keys && data2.rows) {
          // Old format with keys and rows
          let keys2 = data2.keys;
          let rows2 = formatRowsToLocaleString(data2.rows);
          items2 = kRowsToItems(rows2, keys2);
        } else if (data2 && data2.activity_slice_revenues) {
          // New format - object with activity_slice_revenues key containing array
          items2 = data2.activity_slice_revenues;
        } else if (Array.isArray(data2)) {
          // Fallback - data is already an array
          items2 = data2;
        } else {
          items2 = [];
        }

        commit("SET_CAMPAIGN_ACTIVITIES", items1);
        commit("SET_CAMPAIGN_ACTIVITY_SLICES", items2);
      }
      dispatch("setPageLoadingStatus", false);
    },

    async getCampaignResourceUtilization(
      { commit, dispatch },
      { campaignId, speciesId, regionId, activityIds }
    ) {
      try {
        const result = await retrieveCampaignMultiResourceUtilization(
          campaignId,
          {
            speciesId,
            regionId,
            activityIds,
          }
        );

        if (!result.success) {
          dispatch("setNewNotification", result);
        } else {
          commit("SET_CAMPAIGN_RESOURCE_UTILIZATION", result.data);
        }
      } catch (error) {
        let notif = {
          type: "error",
          text: "Fallo al tratar de traer la utilización de recursos",
        };
        dispatch("setNewNotification", notif);
        console.log("GET masterRU failed!");
      }
    },

    async getEngagementActivitySlices(
      { commit, dispatch },
      { engagementId, startYear, startMonth, endYear, endMonth } = {}
    ) {
      dispatch("setPageLoadingStatus", true);

      try {
        if (!engagementId) {
          throw new Error("engagementId is required");
        }

        const baseEndpoint = "module/get";
        
        // endpoint1: Cards endpoint for activity slice cards display
        const endpoint1 = `/${baseEndpoint}/project-cards/engagement/${engagementId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}`;
        // endpoint2: View endpoint for t3Metrics table data
        const endpoint2 = `/${baseEndpoint}/polars-views/engagement_activity_slice_summary?limit=10000&q=engagement_id:${engagementId}`;
        // endpoint3: Aggregated endpoint for total row in t3Metrics
        const endpoint3 = `/${baseEndpoint}/polars-views/agg_engagement_activity_slice_summary?limit=10000&q=engagement_id:${engagementId}`;

        const [sliceResponse, viewResponse, aggResponse] = await Promise.all([
          getRequest(endpoint1, true),
          getRequest(endpoint2, true),
          getRequest(endpoint3, true),
        ]);

        // Check if any request failed
        if (!sliceResponse.success || !viewResponse.success || !aggResponse.success) {
          dispatch(
            "setNewNotification",
            !sliceResponse.success ? sliceResponse : (!viewResponse.success ? viewResponse : aggResponse)
          );
          return;
        }

        // Process data
        const processCardsData = ({ data }) => {
          // New cards endpoint returns data.cards
          let items = data.cards || [];
          // Convert snake_case to camelCase
          return keysToCamel(items);
        };

        const processViewData = ({ data }) => {
          // Polars API returns data as array of objects in response.data.data
          let items = data.data || [];
          // Convert snake_case to camelCase
          return keysToCamel(items);
        };

        const processAggData = ({ data }) => {
          // Polars API returns data as array of objects in response.data.data
          let items = data.data || [];
          // Convert snake_case to camelCase
          return keysToCamel(items);
        };

        const cardsData = processCardsData(sliceResponse);
        const viewData = processViewData(viewResponse);
        const aggData = processAggData(aggResponse);

        // Merge cards data with aggregate data by activitySliceId for activity cards display
        // Cards have the new fields (totalProfit, totalProfitHa, physicalHectaresRatio)
        // Aggregate has the cost breakdown fields needed by charts
        const mergedData = cardsData.map(card => {
          const aggItem = aggData.find(agg => agg.activitySliceId === card.activitySliceId);
          return {
            ...aggItem, // Start with aggregate data (has all cost fields)
            ...card,    // Override with card data (has new profit fields)
          };
        });

        // Store cards data for activity cards display
        commit("SET_ENGAGEMENT_ACTIVITY_SLICES", mergedData);
        // Store view endpoint data for t3Metrics table
        commit("SET_ENGAGEMENT_T3_METRICS_DATA", viewData);
        // Store aggregated data for total row in t3Metrics
        commit("SET_AGG_ENGAGEMENT_T3_METRICS", aggData);
      } catch (error) {
        dispatch("setNewNotification", {
          success: false,
          message: error.message,
        });
      } finally {
        dispatch("setPageLoadingStatus", false);
      }
    },
    async getActivitySlices({ commit, dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);

      let filterKey = payload?.filterKey;
      let filterValue = payload?.filterValue;
      let endpoint = `/views/activity_slice_summary?limit=10000`;

      if (filterKey && filterValue) {
        endpoint = `/views/activity_slice_summary?limit=10000&q=${filterKey}:${filterValue}`;
      }

      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let data = response.data;
        let keys = data.keys;
        let rows = formatRowsToLocaleString(data.rows);
        let items = kRowsToItems(rows, keys);
        if (items.length) {
          let masterRU = { rows: [], keys: [] };
          try {
            // Apply the same comma-removal pattern used elsewhere in the app
            const sliceIds = items.map((i) => parseInt(String(i.activitySliceId).replace(/,/g, '')));
            
            masterRU = await retrieveMultiResourceUtilization(sliceIds);
            if (!masterRU.success) {
              dispatch("setNewNotification", masterRU);
            } else {
              masterRU = masterRU.data;
            }
          } catch {
            let notif = {
              type: "error",
              text: "Fallo al tratar de traer la utilización de recursos",
            };
            dispatch("setNewNotification", notif);
            console.log("GET masterRU failed!");
          }

          commit("SET_ACTIVITY_SLICES", items);
        }
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getDraftEngagementActivitySlices({ commit, dispatch }, payload, dontSetLoading) {
      if (!dontSetLoading) {
        dispatch("setPageLoadingStatus", true);
      }
      
      try {
        const { draftEngagementId, startYear, startMonth, endYear, endMonth } = payload || {};
        
        if (!draftEngagementId) {
          throw new Error("draftEngagementId is required");
        }

        let masterRU = { rows: [], keys: [] };
        const baseEndpoint = "module/get";
        
        // endpoint1: Cards endpoint for activity slice cards display
        let endpoint1 = `/${baseEndpoint}/draft-cards/engagement/${draftEngagementId}?start_year=${startYear}&start_month=${startMonth}&end_year=${endYear}&end_month=${endMonth}`;
        // endpoint2: View endpoint for t3Metrics table data
        let endpoint2 = `/views/draft_engagement_activity_slice_summary?limit=10000&q=draft_engagement_id:${draftEngagementId}`;
        // endpoint3: Aggregated endpoint for total row in t3Metrics
        let endpoint3 = `/views/agg_draft_engagement_activity_slice_summary?limit=10000&q=draft_engagement_id:${draftEngagementId}`;

        let [response1, response2, response3] = await Promise.all([
          getRequest(endpoint1, true),
          getRequest(endpoint2, true),
          getRequest(endpoint3, true),
        ]);
        
        if (!response1.success) {
          dispatch("setNewNotification", response1);
        } else if (!response2.success) {
          dispatch("setNewNotification", response2);
        } else if (!response3.success) {
          dispatch("setNewNotification", response3);
        } else {
          // Process cards data
          let items1 = keysToCamel(response1.data.cards || []);

          // Fetch forwardYield and forwardPrice for each activity slice
          // The cards endpoint doesn't include these fields, so we need to fetch them from individual slice records
          if (items1.length > 0) {
            const sliceDataPromises = items1.map(async (item) => {
              const draftActivitySliceId = parseInt(String(item.activitySliceId).replace(/,/g, ''));

              try {
                const sliceResponse = await getRequest(`/draft_activity_slice/${draftActivitySliceId}`, false);
                if (sliceResponse.success && sliceResponse.data) {
                  const sliceData = sliceResponse.data;

                  // Merge forwardYield, forwardPrice, and activityId into the item
                  // Note: The API returns activityPrice, but the UI uses forwardPrice
                  // Map activityPrice to forwardPrice for the table display
                  // activityId is needed for soy bean price synchronization
                  return {
                    ...item,
                    draftActivitySliceId: draftActivitySliceId,
                    forwardYield: sliceData.forwardYield ?? null,
                    forwardPrice: sliceData.activityPrice ?? sliceData.forwardPrice ?? null,
                    activityPrice: sliceData.activityPrice ?? item.activityPrice ?? null,
                    activityId: sliceData.activityId ?? item.activityId ?? null,
                  };
                } else {
                  console.warn(`Failed to fetch draft slice ${draftActivitySliceId}:`, sliceResponse);
                  return {
                    ...item,
                    draftActivitySliceId: draftActivitySliceId,
                    forwardYield: null,
                    forwardPrice: null,
                  };
                }
              } catch (error) {
                console.error(`Error fetching draft slice ${draftActivitySliceId}:`, error);
                return {
                  ...item,
                  draftActivitySliceId: draftActivitySliceId,
                  forwardYield: null,
                  forwardPrice: null,
                };
              }
            });

            items1 = await Promise.all(sliceDataPromises);
          }
          
          // Process view endpoint data for t3Metrics table (response2)
          let data2 = response2.data;
          let keys2 = data2.keys;
          let rows2 = formatRowsToLocaleString(data2.rows);
          let items2 = kRowsToItems(rows2, keys2);
          
          // Process aggregate data for total row (response3)
          let data3 = response3.data;
          let keys3 = data3.keys;
          let rows3 = formatRowsToLocaleString(data3.rows);
          let items3 = kRowsToItems(rows3, keys3);

          // Store cards data for activity cards display
          commit("SET_DRAFT_ENGAGEMENT_ACTIVITY_SLICES", items1);
          // Store view endpoint data for t3Metrics table
          commit("SET_DRAFT_ENGAGEMENT_T3_METRICS_DATA", items2);
          // Store aggregated data for total row in t3Metrics
          commit("SET_AGG_DRAFT_ENGAGEMENT_T3_METRICS", items3);

          if (items1.length) {
            // Resource utilization is only rendered in the cashflows tab, so
            // it is fetched in the background (intelligent deferring, same
            // pattern as the activity slice pages) instead of blocking the
            // activity cards commit above.
            // Apply the same comma-removal pattern used elsewhere in the app
            const sliceIds = items1.map((i) => parseInt(String(i.activitySliceId || i.draftActivitySliceId).replace(/,/g, '')));

            retrieveDraftMultiResourceUtilization(sliceIds)
              .then((ruResponse) => {
                if (!ruResponse.success) {
                  dispatch("setNewNotification", ruResponse);
                } else {
                  commit("SET_DRAFT_ENGAGEMENT_RESOURCE_UTILIZATION", ruResponse.data);
                }
              })
              .catch(() => {
                let notif = {
                  type: "error",
                  text: "Fallo al tratar de traer la utilización de recursos",
                };
                dispatch("setNewNotification", notif);
                console.log("GET masterRU failed!");
              });
          } else {
            commit("SET_DRAFT_ENGAGEMENT_RESOURCE_UTILIZATION", masterRU);
          }
        }
      } catch (error) {
        console.error("ERROR in getDraftEngagementActivitySlices:", error);
        dispatch("setNewNotification", {
          success: false,
          message: error.message,
        });
      }

      dispatch("setPageLoadingStatus", false);
    },
    async getDraftActivitySlices({ commit, dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);

      let filterKey = payload?.filterKey;
      let filterValue = payload?.filterValue;
      let endpoint = `/views/draft_activity_slice_summary?limit=10000`;

      if (filterKey && filterValue) {
        endpoint = `/views/draft_activity_slice_summary?limit=10000&q=${filterKey}:${filterValue}`;
      }

      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let data = response.data;
        let keys = data.keys;
        let rows = formatRowsToLocaleString(data.rows);
        let items = kRowsToItems(rows, keys);
        if (items.length) {
          commit("SET_DRAFT_ACTIVITY_SLICES", items);
        }
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getEstablishments({ commit, dispatch }) {
      return dedupeInFlight("getEstablishments", async () => {
        dispatch("setPageLoadingStatus", true);

        // Establishments and regions are independent requests.
        let [response, regionsRes] = await Promise.all([
          getRequest("/establishment?limit=1000", false),
          getRequest("/region?limit=1000", false),
        ]);
        if (!regionsRes.success || !response.success) {
          if (!response.success) {
            dispatch("setNewNotification", response);
          }
          if (!regionsRes.success) {
            dispatch("setNewNotification", regionsRes);
          }
        } else {
          let regions = regionsRes.data;

          let data = response.data;

          for (let i = 0; i < data.length; i++) {
            const establishment = data[i];

            let rId = establishment.regionId;
            let regionName = regions.find((i) => i.id === rId)?.name;

            establishment.regionName = regionName;
            data[i] = establishment;
          }

          commit("SET_ESTABLISHMENTS", data);
          commit("SET_REGIONS", regions);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    async getSingleEstablishment({ commit, dispatch }, establishmentId) {
      dispatch("setPageLoadingStatus", true);

      let response = await getRequest(
        "/establishment/" + establishmentId,
        false
      );

      let regionsRes = await getRequest("/region?limit=1000", false);

      if (!regionsRes.success || !response.success) {
        if (!response.success) {
          dispatch("setNewNotification", response);
        }
        if (!regionsRes.success) {
          dispatch("setNewNotification", regionsRes);
        }
      } else {
        let regions = regionsRes.data;

        let establishment = response.data;

        let rId = establishment.regionId;
        let regionName = regions.find((i) => i.id === rId)?.name;

        establishment.regionName = regionName;

        establishment.region = {
          id: establishment.regionId,
          name: establishment.regionName,
        };

        commit("SET_SINGLE_ESTABLISHMENT", establishment);
        commit("SET_REGIONS", regions);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getEngagements({ commit, dispatch }) {
      dispatch("setPageLoadingStatus", true);
      let engagementResponse = await getRequest("/engagement", false);
      if (engagementResponse.success) {
        commit("SET_ENGAGEMENTS", engagementResponse.data);
      } else {
        dispatch("setNewNotification", engagementResponse);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getSingleEngagement({ commit, dispatch }, id) {
      dispatch("setPageLoadingStatus", true);

      // Add debugging to see what ID we're working with
      console.log("getSingleEngagement called with ID:", id, "Type:", typeof id);

      // Ensure ID is a valid integer
      const engagementId = parseInt(id);
      if (isNaN(engagementId)) {
        console.error("Invalid engagement ID:", id);
        dispatch("setNewNotification", {
          data: {
            type: "error",
            text: `Invalid engagement ID: ${id}`,
          },
        });
        dispatch("setPageLoadingStatus", false);
        return;
      }

      let engagementResponse = await getRequest("/engagement/" + engagementId, false);

      if (!engagementResponse.success) {
        dispatch("setNewNotification", engagementResponse);
      } else {
        let engagement = {
          ...engagementResponse.data,
          rentPaymentPlan: [],
          rentFixedRows: [],
          rentBonuses: [],
        };
        let contract = CONTRACTS?.find(
          (el) => el.value == engagement.contractType
        );

        if (contract) {
          // Rents
          // This will make requests based on the contract type used by the engagement
          for (const endpointInfo of contract?.endpoints) {
            let finalEndpoint =
              endpointInfo?.endpoint + "?q=engagement_id:" + engagementId;
            let rowsResult = await getRequest(finalEndpoint);

            if (!rowsResult?.success) {
              this.$store.dispatch("setNewNotification", rowsResult);
            } else {
              engagement[endpointInfo?.key] = rowsResult?.data.sort(
                (a, b) =>
                  new Date(a?.[endpointInfo?.orderBy]) -
                  new Date(b?.[endpointInfo?.orderBy])
              );
            }
          }
        }
        commit("SET_SINGLE_ENGAGEMENT", engagement);

        dispatch("getSingleCampaign", engagement.campaignId);
        dispatch("getSingleEstablishment", engagement.establishmentId);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getSingleEngagementLineage({ commit }, engagementId) {
      // Handle lineage response properly - if it fails, use empty data
      let lineageData = null;
      try {
        let engagementLineageResponse = await getRequest(
          "/module/get/engagement_lineage?engagement_id=" + engagementId,
          false
        );

        if (engagementLineageResponse.success) {
          lineageData = engagementLineageResponse.data;
        } else {
          console.warn("Failed to load engagement lineage:", engagementLineageResponse);
          // Don't show error notification for lineage failures, just log and continue
          lineageData = null;
        }
      } catch (error) {
        console.error("Error calling engagement lineage endpoint:", error);
        // Continue without lineage data if there's an error
        lineageData = null;
      }

      commit("SET_SINGLE_ENGAGEMENT_LINEAGE", lineageData);
    },
    async getParentDraftEngagement({ commit, dispatch }, id) {
      dispatch("setPageLoadingStatus", true);
      let engagementResponse = await getRequest(
        "/draft_engagement/" + id,
        false
      );

      if (!engagementResponse.success) {
        // If 404, it means parent was deleted, else error
        if (engagementResponse?.data?.status != 404) {
          dispatch("setNewNotification", engagementResponse);
        }
      } else {
        let engagement = engagementResponse.data;

        commit("SET_SINGLE_DRAFT_ENGAGEMENT", engagement);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getCompleteSingleDraftEngagement({ commit, dispatch }, id, dontSetLoading) {
      if (!dontSetLoading) {
        dispatch("setPageLoadingStatus", true);
      }
      let engagementResponse = await getRequest(
        "/draft_engagement/" + id,
        false,
      );
      if (!engagementResponse.success) {
        dispatch("setNewNotification", engagementResponse);
      } else {
        let engagement = {
          ...engagementResponse.data,
          rentPaymentPlan: [],
          rentFixedRows: [],
          rentBonuses: [],
        };
        let contract = DRAFT_CONTRACTS?.find(
          (el) => el.value == engagement.contractType,
        );
        // The lock status request is independent from the rent requests, so it
        // is started up-front and awaited at the end.
        let lockPromise = getDraftLock(id);
        if (contract) {
          // Rents
          // This will make requests based on the contract type used by the
          // engagement. The endpoints are independent, so they are requested
          // in parallel instead of sequentially.
          let rentResults = await Promise.all(
            contract?.endpoints.map(async (endpointInfo) => {
              let finalEndpoint =
                endpointInfo?.endpoint + "?q=draft_engagement_id:" + id;
              return { endpointInfo, rowsResult: await getRequest(finalEndpoint) };
            }),
          );
          for (const { endpointInfo, rowsResult } of rentResults) {
            if (!rowsResult?.success) {
              dispatch("setNewNotification", rowsResult);
            } else {
              engagement[endpointInfo?.key] = rowsResult?.data.sort(
                (a, b) =>
                  new Date(a?.[endpointInfo?.orderBy]) -
                  new Date(b?.[endpointInfo?.orderBy]),
              );
            }
          }
        }

        // Now get the lock status if it exists
        engagement.locked = await lockPromise;

        commit("SET_SINGLE_DRAFT_ENGAGEMENT", engagement);
        dispatch("getSingleEstablishment", engagement?.establishmentId);
      }
      if (!dontSetLoading) {
        dispatch("setPageLoadingStatus", false);
      }
    },
    async clearCampaignEngagements({ commit }) {
      commit("SET_CAMPAIGN_ENGAGEMENTS", []);
    },
    async getCampaignEngagements({ commit, dispatch }, campaignId) {
      let queryArg = campaignId ? `q=campaign_id:${campaignId}` : "";

      dispatch("setPageLoadingStatus", true);

      let response = await getRequest(
        "/module/get/polars-views/enriched_campaign_engagements?limit=1000&" + queryArg,
        true
      );
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        // New Polars API returns data as array of objects in response.data.data
        let items = response.data.data || [];
        
        // Convert snake_case to camelCase
        items = keysToCamel(items);

        // Extract keys from the first item if available
        let keys = items.length > 0 ? Object.keys(items[0]) : [];

        // Format data object to match expected structure
        let data = {
          items: items,
          keys: keys.map((i) => {
          return { text: i, value: i };
          }),
          total: response.data.total,
        };

        commit("SET_CAMPAIGN_ENGAGEMENTS", data);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getDraftEngagementsByIdArray({ dispatch }, draftEngagementsIdArray) {
      let endpoint = "/views/enriched_draft_engagements?limit=1000";
      let dataOfArraysToCompare = [];

      for (const id of draftEngagementsIdArray) {
        let endpointFiltered = endpoint + `&q=draft_engagement_id:${id}`;
        let response = await getRequest(endpointFiltered, true);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          let data = response.data;
          let keys = [...data.keys];
          let items = kRowsToItems(data.rows, keys);

          delete data.rows;

          dataOfArraysToCompare.push(...items);
        }
      }
      dispatch("setDraftEngagementsToCompare", dataOfArraysToCompare);
    },
    async getCampaignVersionByIdArray({ dispatch }, campaignVersionsIdArray) {
      let endpoint = "/campaign";
      let dataOfArraysToCompare = [];
      for (const id of campaignVersionsIdArray) {
        let finalEndpoint = endpoint + `/${id}`;
        let response = await getRequest(finalEndpoint, true);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          let data = response.data;
          dataOfArraysToCompare.push(data);
        }
      }
      dispatch("setCampaignVersionsToCompare", dataOfArraysToCompare);
    },
    async getDraftEngagementsAttachmentAnalysis(
      { commit, dispatch },
      params = {}
    ) {
      dispatch("setPageLoadingStatus", true);
      let endpoint = "/views/draft_engagement_attachment_status?limit=1000";

      let filters = [];

      if (params.establishmentId) {
        filters.push(`establishment_id:${params.establishmentId}`);
      }

      if (params.abstractCampaignId) {
        filters.push(`abstract_campaign_id:${params.abstractCampaignId}`);
      }

      if (params.draftEngagementId) {
        filters.push(`draft_engagement_id:${params.draftEngagementId}`);
      }

      // Modified this condition to check for zero explicitly
      if (
        params.attachedCampaignVersion !== undefined &&
        params.attachedCampaignVersion !== null
      ) {
        filters.push(
          `attached_campaign_version:${params.attachedCampaignVersion}`
        );
      }

      if (filters.length > 0) {
        endpoint += `&q=${filters.join(",")}`;
      }

      console.log("Final endpoint:", endpoint);

      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_DRAFT_ENGAGEMENTS_ATTACHMENT_ANALYSIS", response.data);
      }
      dispatch("setPageLoadingStatus", false);
    },

    async getDraftEngagements({ commit, dispatch, state }, params = {}) {
      console.log("========= getDraftEngagements START =========");
      console.log("Received params:", params);

      dispatch("setPageLoadingStatus", true);
      let endpoint = "/views/enriched_draft_engagements?limit=1000";

      let filters = [];

      // Note: We'll handle manager filtering on the client side instead of API level
      // to avoid complex queries like (establishment_id:11,establishment_id:21,...)
      const userType = state.userTokenInfo?.utype;

      if (params.establishmentId) {
        filters.push(`establishment_id:${params.establishmentId}`);
        console.log(`Added establishment filter: establishment_id:${params.establishmentId}`);
      }

      if (params.hasBeenAttached) {
        // params.hasBeenAttached should be "true" or "false" string
        const attachmentValue =
          params.hasBeenAttached === "true" ? "Yes" : "No";
        filters.push(`has_been_attached:${attachmentValue}`);
        console.log(`Added attachment filter: has_been_attached:${attachmentValue}`);
      }

      // Handle abstractCampaignId filter, including special case for id=0
      if (params.abstractCampaignId !== null && params.abstractCampaignId !== undefined) {
        console.log(`Processing abstractCampaignId filter with value: ${params.abstractCampaignId}`);
        if (params.abstractCampaignId === 0) {
          // Special case: id=0 means "no campaign associated"
          filters.push(`abstract_campaign_id:null`);
          console.log(`Added campaign filter for "no campaign": abstract_campaign_id:null`);
        } else {
          filters.push(`abstract_campaign_id:${params.abstractCampaignId}`);
          console.log(`Added campaign filter: abstract_campaign_id:${params.abstractCampaignId}`);
        }
      }

      if (filters.length > 0) {
        endpoint += `&q=${filters.join(",")}`;
      }

      console.log("Final endpoint:", endpoint);

      let response = await getRequest(endpoint, true);
      if (!response.success) {
        dispatch("setNewNotification", response);
        console.log("API request failed:", response);
      } else {
        let data = response.data;
        let keys = [...data.keys];
        let items = kRowsToItems(data.rows, keys);

        console.log("Received draft engagements items count:", items.length);

        // For managers, filter the results on the client side
        if (userType === "manager") {
          const userEstablishmentList = state.userTokenInfo?.estlist ||
            state.userTokenInfo?.eslist ||
            state.userTokenInfo?.establishments ||
            [];
          console.log("Filtering results for manager with establishments:", userEstablishmentList.length);

          if (userEstablishmentList.length > 0) {
            const filteredItems = items.filter(item =>
              userEstablishmentList.includes(item.establishmentId)
            );
            console.log(`Filtered from ${items.length} to ${filteredItems.length} items for manager`);
            items = filteredItems;
          } else {
            console.log("No establishment list for manager, showing no items");
            items = [];
          }
        }

        data.items = items;
        delete data.rows;

        data.keys = data.keys.map((i) => {
          return { text: i, value: i };
        });

        commit("SET_DRAFT_ENGAGEMENTS", data);
      }
      console.log("========= getDraftEngagements END =========");
      dispatch("setPageLoadingStatus", false);
    },
    async getCostItemFamily({ commit, dispatch }) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest("/cost_item_family?limit=1000", false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let costItemFamilies = response.data;
        commit("SET_COST_ITEM_FAMILIES", costItemFamilies);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getAllCostItems({ commit, dispatch }) {
      return dedupeInFlight("getAllCostItems", async () => {
        dispatch("setPageLoadingStatus", true);

        let [r1, r3] = await Promise.all([
          getRequest("/direct_slice_cost_item?limit=5000", false),
          getRequest("/cost_item_family", false),
        ]);

        if (!r1.success || !r3.success) {
          if (!r1.success) {
            dispatch("setNewNotification", r1);
          }
          if (!r3.success) {
            dispatch("setNewNotification", r3);
          }
        } else {
          let directSliceCostItems = r1.data;
          // Lookup the cost item family names
          let costItemFamilies = r3.data;

          for (let i = 0; i < directSliceCostItems.length; i++) {
            const item = directSliceCostItems[i];
            let family_id = item.costItemFamilyId;
            let family = costItemFamilies.find((i) => i.id === family_id);
            directSliceCostItems[i].costItemFamilyName = family.name;
          }

          commit("SET_DIRECT_SLICE_COST_ITEMS", directSliceCostItems);
          commit("SET_COST_ITEM_FAMILIES", costItemFamilies);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    async getSingleAbstractCampaign({ commit, dispatch }, id) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest(`/abstract_campaign/${id}`, false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let abstractCampaign = response.data;
        commit("SET_SINGLE_ABSTRACT_CAMPAIGN", abstractCampaign);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getAbstractCampaigns({ commit, dispatch }) {
      return dedupeInFlight("getAbstractCampaigns", async () => {
        dispatch("setPageLoadingStatus", true);
        let response = await getRequest("/abstract_campaign", false);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          let abstractCampaigns = response.data;
          commit("SET_ABSTRACT_CAMPAIGNS", abstractCampaigns);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    async getCampaigns({ commit, dispatch }, abstractCampaignId) {
      return dedupeInFlight(`getCampaigns:${abstractCampaignId ?? "all"}`, async () => {
        let endpoint = "/campaign";
        if (abstractCampaignId) {
          endpoint =
            endpoint +
            "?q=" +
            `abstract_campaign_id:${abstractCampaignId}` +
            "&limit=1000";
        } else {
          endpoint = endpoint + "?limit=1000";
        }
        dispatch("setPageLoadingStatus", true);
        let response = await getRequest(endpoint, false);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          let campaigns = response.data;
          commit("SET_CAMPAIGNS", campaigns);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    async getSingleCampaign({ commit, dispatch }, campaignId) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest("/campaign/" + campaignId, false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let campaign = response.data;
        commit("SET_SINGLE_CAMPAIGN", campaign);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getActivities({ commit, dispatch }) {
      return dedupeInFlight("getActivities", async () => {
        dispatch("setPageLoadingStatus", true);
        let response = await getRequest("/activity", false);

        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          commit("SET_ACTIVITIES", response.data);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    async getSpecies({ commit, dispatch }) {
      return dedupeInFlight("getSpecies", async () => {
        dispatch("setPageLoadingStatus", true);
        let response = await getRequest("/species", false);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          commit("SET_SPECIES", response.data);
        }
        dispatch("setPageLoadingStatus", false);
      });
    },
    unsetCampaignModifiers({ commit }) {
      commit("SET_CAMPAIGN_MODIFIERS", []);
    },
    unsetFilteredCampaignModifiers({ commit }) {
      commit("SET_FILTERED_CAMPAIGN_MODIFIERS", []);
    },
    async getCampaignModifiers({ commit, dispatch }, campaignId) {
      dispatch("setPageLoadingStatus", true);
      let q = `?q=campaign_id:${campaignId}&limit=5000`;
      let response = await getRequest(
        "/simulation_modifier_cost_item_family/" + q,
        false
      );
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let campaignMods = response.data;
        commit("SET_CAMPAIGN_MODIFIERS", campaignMods);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getFilteredCampaignModifiers({ commit, dispatch }, filters) {
      dispatch("setPageLoadingStatus", true);
      let q = "";
      let campaignId = filters?.campaignId;
      let regionId = filters?.regionId;
      let speciesId = filters?.speciesId;
      let costItemFamilyId = filters?.costItemFamilyId;
      let modifier = filters?.modifier;

      let obj = {
        campaignId: campaignId ? `campaign_id:${campaignId}` : "",
        regionId: regionId ? `region_id:${regionId}` : "",
        speciesId: speciesId ? `species_id:${speciesId}` : "",
        costItemFamilyId: costItemFamilyId
          ? `cost_item_family_id:${costItemFamilyId}`
          : "",
        modifier: modifier ? `modifier:${modifier}` : "",
      };

      let parsedFilters = Object.values(obj).filter(Boolean).join(",");
      if (parsedFilters) {
        q = `?q=${parsedFilters}`;
      }
      let response = await getRequest(
        "/simulation_modifier_cost_item_family/" + q,
        false
      );
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        let campaignMods = response.data;
        commit("SET_FILTERED_CAMPAIGN_MODIFIERS", campaignMods);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async fetchSliceProductsList({ commit, dispatch }) {
      let sliceProductsList = await getSliceProductsList();
      if (!sliceProductsList.success) {
        dispatch("setNewNotification", sliceProductsList);
      } else {
        sliceProductsList = sliceProductsList.data;
        commit("SET_SLICE_PRODUCTS_LIST", sliceProductsList);
      }
    },
    async fetchSliceCostInputPeriods({ commit, dispatch }) {
      return dedupeInFlight("fetchSliceCostInputPeriods", async () => {
        let periods = await getRequest("/cost_input_period");
        if (!periods.success) {
          dispatch("setNewNotification", periods);
        } else {
          periods = periods.data;
          commit("SET_SLICE_COST_INPUT_PERIODS", periods);
        }
      });
    },
    async updateSASliceCache({ commit, dispatch, getters }, key) {
      // Action used to update single activity slice without having to update all of it
      let oldAs = getters.singleActivitySlice;
      let newAs = await updateSingleActivitySliceCache(oldAs, key);
      if (!newAs.success) {
        for (const error of newAs.errorDataArray) {
          dispatch("setNewNotification", error);
        }
      } else {
        commit("SET_SINGLE_ACTIVITY_SLICE", newAs);
      }
    },
    async updateDraftSASliceCache({ commit, dispatch, getters }, key) {
      let oldDAs = getters.singleDraftActivitySlice;
      let newDAs = await updateSingleDraftActivitySliceCache(oldDAs, key);
      if (!newDAs.success) {
        for (const error of newDAs.errorDataArray) {
          dispatch("setNewNotification", error);
        }
      } else {
        commit("SET_SINGLE_DRAFT_ACTIVITY_SLICE", newDAs);
      }
    },
    async fetchCostGroups({ commit, dispatch }) {
      let response = await getRequest("/stored_cost_item_group", false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_COST_ITEM_GROUPS", response.data);
      }
    },
    async fetchCostGroupElements({ commit, dispatch }) {
      let response = await getRequest("/stored_cost_item_group_element", false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_SINGLE_COST_GROUP_ELEMENTS", response.data);
      }
    },
    async fetchFinancialPolicies({ commit, dispatch }) {
      return dedupeInFlight("fetchFinancialPolicies", async () => {
        let response = await getRequest("/payment_policy?limit=1000", false);
        if (!response.success) {
          dispatch("setNewNotification", response);
        } else {
          commit("SET_FINANCIAL_POLICIES", response.data);
        }
      });
    },

    async fetchOtherTonCostItems({ commit, dispatch }) {
      let response = await getRequest("/other_ton_cost_item", false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_OTHER_TON_COST_ITEMS", response.data);
      }
    },

    async updateFinancialPolicies({ dispatch }, payload) {
      let response = await putRequest("/payment_policy/" + payload.id, {
        name: payload.name,
        policyJson: JSON.stringify(payload.data),
      });

      console.log(`updateFinancialPolicies ${response.status}`);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        dispatch("fetchFinancialPolicies");
      }
    },

    async fetchInterestRates({ commit, dispatch }) {
      return dedupeInFlight("fetchInterestRates", async () => {
        let response = await getRequest("/interest_rate", false);
        if (!response.success) {
          dispatch("setNewNotification", response);
          throw new Error("Could not fetch interest rates");
        } else {
          let rates = alphabetized(
            response.data.map((i) => {
              return {
                ...i,
                name: `${i.name} (${i.rate})`,
              };
            }),
            "name",
          );

          // Keep the "no financing" option last, outside the alphabetical order
          rates.push({
            name: "No financiar",
            id: null,
            rate: 0.0,
          });
          commit("SET_INTEREST_RATES", rates);
        }
      });
    },
    async getEngagementResourceUtilization({ commit, dispatch }, { sliceIds }) {
      let masterRU = { rows: [], keys: [] };
      try {
        masterRU = await retrieveMultiResourceUtilization(sliceIds);

        if (!masterRU.success) {
          dispatch("setNewNotification", masterRU);
        } else {
          masterRU = masterRU.data;
          commit("SET_ENGAGEMENT_RESOURCE_UTILIZATION", masterRU);
        }
      } catch {
        let notif = {
          type: "error",
          text: "Fallo al tratar de traer la utilización de recursos",
        };
        dispatch("setNewNotification", notif);
        console.log("GET masterRU failed!");
      }
    },
    async getApiKeys({ commit, dispatch }) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest("/api_key");
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_API_KEYS", response.data);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async createApiKey({ dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);
      let response = await postRequest("/api_key", payload);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        // Refresh the full list of keys
        dispatch("getApiKeys");
        dispatch("setNewNotification", {
          data: {
            type: "success",
            text: "API key created successfully",
          },
        });
      }
      dispatch("setPageLoadingStatus", false);
    },
    async deleteApiKey({ commit, dispatch }, apiKeyId) {
      dispatch("setPageLoadingStatus", true);
      let response = await deleteRequest(`/api_key/${apiKeyId}`);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("REMOVE_API_KEY", apiKeyId);
        dispatch("setNewNotification", {
          data: {
            type: "success",
            text: "API key deleted successfully",
          },
        });
      }
      dispatch("setPageLoadingStatus", false);
    },
    async putCampaign({ dispatch }, payload) {
      let result = await putRequest("/campaign/" + payload.id, payload);
      if (!result.success) {
        dispatch("setNewNotification", result);
      } else {
        dispatch("getSingleCampaign", payload.id);
      }
    },
    async deleteEstablishment({ dispatch }, establishmentId) {
      dispatch("setPageLoadingStatus", true);

      let response = await deleteRequest(`/establishment/${establishmentId}`);

      if (!response.success) {
        // Just pass through the response directly - the error handler will format it properly
        dispatch("setNewNotification", response);
      }

      dispatch("setPageLoadingStatus", false);
      return response;
    },
    async getSingleActivity({ commit, dispatch }, activityId) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest(`/activity/${activityId}`, false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_SINGLE_ACTIVITY", response.data);
      }
      dispatch("setPageLoadingStatus", false);
    },

    async updateActivity({ dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);
      let response = await putRequest(`/activity/${payload.id}`, payload);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        dispatch("getSingleActivity", payload.id);
        dispatch("setNewNotification", {
          data: {
            type: "success",
            text: "Activity updated successfully",
          },
        });
      }
      dispatch("setPageLoadingStatus", false);
      return response;
    },

    async deleteActivity({ dispatch }, activityId) {
      dispatch("setPageLoadingStatus", true);
      let response = await deleteRequest(`/activity/${activityId}`);
      if (!response.success) {
        dispatch("setNewNotification", response);
      }
      dispatch("setPageLoadingStatus", false);
      return response;
    },

    async getSingleSpecies({ commit, dispatch }, speciesId) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest(`/species/${speciesId}`, false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_SINGLE_SPECIES", response.data);
      }
      dispatch("setPageLoadingStatus", false);
    },

    async updateSpecies({ dispatch }, payload) {
      dispatch("setPageLoadingStatus", true);
      let response = await putRequest(`/species/${payload.id}`, payload);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        dispatch("getSingleSpecies", payload.id);
        dispatch("setNewNotification", {
          data: {
            type: "success",
            text: "Species updated successfully",
          },
        });
      }
      dispatch("setPageLoadingStatus", false);
      return response;
    },

    async deleteSpecies({ dispatch }, speciesId) {
      dispatch("setPageLoadingStatus", true);
      let response = await deleteRequest(`/species/${speciesId}`);
      if (!response.success) {
        dispatch("setNewNotification", response);
      }
      dispatch("setPageLoadingStatus", false);
      return response;
    },
    // Add this new action
    async updateCampaignResourceUtilization(
      { commit, dispatch },
      { campaignId, speciesId, regionId, activityIds }
    ) {
      try {
        const result = await retrieveCampaignMultiResourceUtilization(
          campaignId,
          {
            speciesId,
            regionId,
            activityIds,
          }
        );

        if (!result.success) {
          dispatch("setNewNotification", result);
        } else {
          commit("SET_CAMPAIGN_RESOURCE_UTILIZATION", result.data);
        }
      } catch (error) {
        let notif = {
          type: "error",
          text: "Fallo al tratar de traer la utilización de recursos",
        };
        dispatch("setNewNotification", notif);
        console.log("GET masterRU failed!");
      }
    },
    async getCostInputPeriods({ commit, dispatch }) {
      dispatch("setPageLoadingStatus", true);
      let response = await getRequest("/cost_input_period", false);
      if (!response.success) {
        dispatch("setNewNotification", response);
      } else {
        commit("SET_COST_INPUT_PERIODS", response.data);
      }
      dispatch("setPageLoadingStatus", false);
    },
    async getActivitySliceOutOfRangeAnalysis(
      { commit, dispatch },
      activitySliceId
    ) {
      let response = await getRequest(
        `/views/out_of_range_activity_slice_costs?q=activity_slice_id:${activitySliceId}`,
        false
      );
      if (!response.success) {
        dispatch("setNewNotification", response.data);
      } else {
        commit(
          "SET_ACTIVITY_SLICE_OUT_OF_RANGE_ANALYSIS",
          kRowsToItems(response.data.rows, response.data.keys, false)
        );
      }
    },
    async getDraftActivitySliceOutOfRangeAnalysis(
      { commit, dispatch },
      draftActivitySliceId
    ) {
      let response = await getRequest(
        `/views/out_of_range_draft_activity_slice_costs?q=draft_activity_slice_id:${draftActivitySliceId}`,
        false
      );
      if (!response.success) {
        dispatch("setNewNotification", response.data);
      } else {
        commit(
          "SET_DRAFT_ACTIVITY_SLICE_OUT_OF_RANGE_ANALYSIS",
          kRowsToItems(response.data.rows, response.data.keys, false)
        );
      }
    },
  },
});
